/**
 * French, and the definition of what every other language must supply.
 *
 * **French is the source language, not a translation.** This is a French
 * financial reporting product: its workflow states, its rubrics and its
 * accounting vocabulary are French terms with legal weight, and translating
 * them into English and back would lose that. So this file is authored, the
 * English one follows it, and the type below is derived from this file so a
 * key added here is a compile error in every other language until it is
 * supplied.
 *
 * Keys are grouped by where the words appear, not by what they mean, because
 * the question somebody has while editing a component is always "what does
 * this panel say", never "where else is the word Save used".
 */

export const fr = {
  common: {
    save: 'Enregistrer',
    cancel: 'Annuler',
    close: 'Fermer',
    delete: 'Supprimer',
    duplicate: 'Dupliquer',
    done: 'Terminer',
    add: 'Ajouter',
    remove: 'Retirer',
    back: 'Retour',
    send: 'Envoyer',
    sending: 'Envoi',
    loading: 'Chargement...',
    problem: 'Une erreur est survenue.',
    unreachable: 'Le serveur est injoignable.',
    saveFailed: 'Ce rapport n’a pas pu être enregistré.',
    tokenUnreadable: 'Ce jeton d’accès n’a pas pu être lu.',
    tokenExpired: 'Ce jeton d’accès a déjà expiré.',
    reportUnavailable: 'Ce rapport n’a pas pu être ouvert.',
  },

  templates: {
    title: 'Nouveau rapport',
    lead: 'Sur quoi partir. Un mod\u00e8le est un point de d\u00e9part : tout ce qu\u2019il met en place vous appartient ensuite, et rien ne le relie au mod\u00e8le.',
    create: 'Cr\u00e9er',
    creating: 'Cr\u00e9ation...',

    reportings: {
      title: 'Reportings CSRD',
      lead: 'Le premier choisi est le principal, le rapport porte sur lui. Les autres sont secondaires.',
      loading: 'Connexion à CSRD...',
      unavailable:
        'CSRD est injoignable, ou n’a aucun reporting. Le rapport sera créé sans reporting.',
      n1: 'N-1 : {code}',
      principal: 'Principal',
      secondaire: 'Secondaire',
      makePrincipal: 'Rendre principal',
      required: 'Choisissez au moins un reporting.',
    },

    previous: {
      name: 'Partir de l’an dernier',
      about:
        'Le rapport de l’an dernier à l’identique, rattaché aux reportings de cette année, avec les chiffres de cette année relus depuis CSRD.',
    },

    previousPick: {
      title: 'Rapport de l’an dernier',
      none: 'Choisir un rapport',
      required: 'Choisissez le rapport de l’an dernier.',
      failed: 'Ce rapport n’a pas pu être ouvert.',
      n1: 'Le reporting principal fait suite à {code} dans CSRD.',
      principal: 'Le reporting principal {from} devient {to}.',
      matchFor: 'Reporting secondaire qui remplace {code}',
      noMatch: 'Aucun',
      orphans:
        'Les tableaux liés à {codes} restent liés à lui et doivent être rebranchés en les important de nouveau.',
    },

    pdf: {
      name: 'Partir d\u2019un PDF',
      about:
        'Les pages d\u2019un rapport existant, en sections modifiables ou exactement comme imprim\u00e9es. Choisissez le fichier ou d\u00e9posez-le ici.',
      choose: 'Choisir un PDF',
    },

    blank: {
      name: 'Rapport vierge',
      about:
        'Une page de garde, un sommaire et un premier chapitre. De quoi commencer sans partir d\u2019une page blanche.',
    },

    financial: {
      name: 'Dossier financier',
      about:
        'L\u2019ordre dans lequel un dossier statutaire est reli\u00e9 : \u00e9tats, annexe, signatures. Les tableaux sont pos\u00e9s, jamais remplis.',
      item: 'Poste',
      current: 'Exercice',
      prior: 'Exercice pr\u00e9c\u00e9dent',
      overview: 'Pr\u00e9sentation',
      overviewTitle: 'Faits marquants de l\u2019exercice',
      overviewBody:
        'R\u00e9sumez ici ce qui a marqu\u00e9 l\u2019exercice : \u00e9volution de l\u2019activit\u00e9, op\u00e9rations significatives, \u00e9v\u00e9nements post\u00e9rieurs \u00e0 la cl\u00f4ture.',
      keyFigures: 'Chiffres cl\u00e9s',
      statements: '\u00c9tats financiers',
      balanceSheet: 'Bilan',
      assets: 'Actif',
      liabilities: 'Passif',
      incomeStatement: 'Compte de r\u00e9sultat',
      income: 'R\u00e9sultat de l\u2019exercice',
      cashFlow: 'Tableau des flux de tr\u00e9sorerie',
      cash: 'Flux de tr\u00e9sorerie',
      notes: 'Annexe',
      basis: 'R\u00e8gles et m\u00e9thodes comptables',
      basisBody:
        'Indiquez le r\u00e9f\u00e9rentiel appliqu\u00e9, les m\u00e9thodes d\u2019\u00e9valuation retenues et tout changement de m\u00e9thode par rapport \u00e0 l\u2019exercice pr\u00e9c\u00e9dent.',
      consolidationScope: 'P\u00e9rim\u00e8tre de consolidation',
      firstNote: 'Note 1',
    },

    financialSustainability: {
      name: 'Rapport financier et de durabilité',
      about:
        'Le rapport financier annuel avec l’état de durabilité intégré au rapport de gestion : gestion, durabilité, vigilance, gouvernement d’entreprise, comptes consolidés et sociaux.',
      item: 'Poste',
      current: 'Exercice N',
      prior: 'Exercice N-1',
      attestationTitle:
        'Attestation des personnes assumant la responsabilité du rapport financier annuel',
      attestationBody:
        'Nous attestons que, à notre connaissance, les comptes sont établis conformément aux normes comptables applicables et donnent une image fidèle du patrimoine, de la situation financière et du résultat de la société et de l’ensemble des entreprises comprises dans le périmètre de consolidation, et que le rapport de gestion présente un tableau fidèle de l’évolution des affaires, des résultats et de la situation financière de la société et de l’ensemble des entreprises comprises dans le périmètre de consolidation ainsi qu’une description des principaux risques et incertitudes auxquels elles sont confrontées, et qu’il a été établi conformément aux normes d’information en matière de durabilité applicables.',
      profile: 'Présentation du Groupe',
      editorial: 'Éditorial',
      editorialBody:
        'Le message du dirigeant : ce qui a marqué l’exercice, les résultats obtenus et le cap fixé pour les années à venir. Signé de son nom et de sa fonction.',
      purpose: 'Raison d’être',
      purposeBody:
        'La raison d’être de l’entreprise, telle qu’inscrite dans ses statuts le cas échéant, et ce qu’elle engage concrètement.',
      keyFigures: 'Chiffres clés',
      trends: 'Grandes tendances',
      trendsBody:
        'Les évolutions de fond qui pèsent sur l’activité : démographie, climat, réglementation, attentes des clients et des collaborateurs. Chaque chiffre avec sa source.',
      valueCreation: 'Modèle de création de valeur',
      valueCreationBody:
        'Les ressources mobilisées, les activités, et la valeur créée pour chaque partie prenante. C’est le point de départ de la description du modèle d’affaires que demande SBM-1.',
      valueChain: 'Chaîne de valeur',
      valueChainBody:
        'Les principaux acteurs en amont et en aval, et la place de l’entreprise entre eux.',
      organisation: 'Organigramme du Groupe',
      management: 'Rapport de gestion',
      activity: 'Activité et résultats de l’exercice',
      highlights: 'Principales évolutions de l’exercice',
      highlightsBody:
        'Les faits marquants par activité et par zone géographique : contrats gagnés ou perdus, acquisitions, cessions, mises en service.',
      revenue: 'Chiffre d’affaires consolidé',
      revenueBody:
        'Son montant, sa variation, et la part due au périmètre, au change et à l’activité à périmètre constant.',
      operatingResult: 'Résultat opérationnel',
      operatingResultBody:
        'Son montant et les principaux effets qui l’expliquent. Précisez l’indicateur retenu (EBITDA, EBIT) et sa définition.',
      netResult: 'Résultat net consolidé, part du Groupe',
      netResultBody:
        'Le passage du résultat opérationnel au résultat net : résultat financier, impôt, quote-part des sociétés mises en équivalence.',
      equity: 'Capitaux propres consolidés',
      equityBody: 'Leur montant à la clôture et leur variation sur l’exercice.',
      investments: 'Investissements',
      investmentsBody:
        'Le montant investi, sa répartition par nature, et la part financée par des tiers.',
      netDebt: 'Endettement net consolidé',
      netDebtBody:
        'Sa définition, son montant, son évolution, et les principales opérations de financement de l’exercice.',
      research: 'Activités en matière de recherche et de développement',
      researchBody: 'Les travaux menés et les moyens qui leur sont consacrés.',
      parentAccounts: 'Comptes sociaux',
      parentAccountsBody:
        'Les résultats de la société mère et leur écart avec les comptes consolidés.',
      outlook: 'Perspectives',
      outlookBody:
        'L’évolution prévisible de la situation et les objectifs annoncés pour l’exercice suivant.',
      subsequentEvents: 'Événements importants survenus depuis la clôture',
      subsequentEventsBody:
        'Ce qui s’est produit entre la date de clôture et l’arrêté des comptes et qui éclaire la situation.',
      fiveYears: 'Résultats de la société au cours des cinq derniers exercices',
      fiveYearsItem: 'Nature des indications',
      fiveYearsCapital: 'Situation financière en fin d’exercice',
      fiveYearsOperations: 'Résultat global des opérations effectives',
      fiveYearsPerShare: 'Résultat des opérations réduit à une seule action',
      fiveYearsStaff: 'Personnel',
      paymentSuppliers: 'Factures reçues non réglées à la date de clôture dont le terme est échu',
      paymentCustomers: 'Factures émises non réglées à la date de clôture dont le terme est échu',
      paymentDelay: 'Tranches de retard de paiement',
      days0: '0 jour (indicatif)',
      days30: '1 à 30 jours',
      days60: '31 à 60 jours',
      days90: '61 à 90 jours',
      days91: '91 jours et plus',
      daysTotal: 'Total',
      invoiceCount: 'Nombre de factures concernées',
      invoiceAmount: 'Montant total des factures concernées',
      shareOfPurchases: 'Pourcentage du montant total des achats de l’exercice',
      shareOfRevenue: 'Pourcentage du chiffre d’affaires de l’exercice',
      internalControl: 'Contrôle interne et gestion des risques',
      controlFramework: 'Cadre de référence',
      controlFrameworkBody:
        'Le référentiel de contrôle interne appliqué et l’organisation en trois lignes de maîtrise : les opérationnels, les fonctions de gestion des risques et de conformité, l’audit interne.',
      controlOrganisation: 'Organisation du dispositif',
      controlOrganisationBody:
        'Les fonctions dédiées (gestion des risques, contrôle interne, audit, assurances, éthique, cybersécurité), leur rattachement et leurs échanges avec les organes de gouvernance.',
      riskFactors: 'Facteurs de risques',
      riskStrategic: 'Risques stratégiques',
      riskOperational: 'Risques opérationnels',
      riskFinancial: 'Risques financiers',
      riskCompliance: 'Risques juridiques et de conformité',
      riskPeople: 'Risques liés aux ressources humaines',
      sustainability: 'Rapport de durabilité',
      general: 'Informations générales',
      basisOfPreparation: 'Base de préparation',
      bp1: 'BP-1 – Base générale pour la préparation des états de durabilité',
      bp1Body:
        'Le périmètre de consolidation retenu et son écart éventuel avec celui des états financiers, la couverture de la chaîne de valeur amont et aval, et le recours aux options d’omission.',
      bp2: 'BP-2 – Publication d’informations relatives à des circonstances particulières',
      bp2Body:
        'Les horizons de temps retenus, les estimations relatives à la chaîne de valeur, les sources d’incertitude, les changements de méthode et les informations incorporées par référence.',
      governance: 'Gouvernance',
      gov1: 'GOV-1 – Le rôle des organes d’administration, de direction et de surveillance',
      gov1Body:
        'Leur composition, leur diversité, leurs compétences en matière de durabilité et la façon dont ils supervisent les impacts, risques et opportunités.',
      gov2: 'GOV-2 – Informations transmises aux organes d’administration, de direction et de surveillance de l’entreprise et enjeux de durabilité traités par ces organes',
      gov2Body:
        'Qui les informe, à quelle fréquence, et quels enjeux ont été examinés au cours de l’exercice.',
      gov3: 'GOV-3 – Intégration des performances en matière de durabilité dans les mécanismes d’incitation',
      gov3Body:
        'Les critères de durabilité retenus dans la rémunération des dirigeants, et leur poids.',
      gov4: 'GOV-4 – Déclaration sur la vigilance raisonnable',
      gov4Body:
        'Une table de correspondance renvoyant, pour chaque étape de la vigilance raisonnable, à l’endroit du rapport où elle est décrite.',
      gov5: 'GOV-5 – Gestion des risques et contrôles internes concernant l’information en matière de durabilité',
      gov5Body:
        'Le dispositif qui fiabilise la collecte et la production des données de durabilité, les principaux risques identifiés et les contrôles qui y répondent.',
      strategy: 'Stratégie',
      sbm1: 'SBM-1 – Stratégie, modèle économique et chaîne de valeur',
      sbm1Body:
        'Les produits, marchés et effectifs par zone géographique, les objectifs de durabilité, et le modèle économique décrit tout au long de la chaîne de valeur.',
      sbm2: 'SBM-2 – Intérêts et points de vue des parties prenantes',
      sbm2Body:
        'Les parties prenantes clés, les modalités du dialogue engagé avec elles, et la façon dont leurs attentes ont été prises en compte.',
      sbm3: 'SBM-3 – Impacts, risques et opportunités matériels et leur lien avec la stratégie et le modèle économique',
      sbm3Body:
        'Les impacts, risques et opportunités retenus, leur place dans la chaîne de valeur, leurs effets actuels et attendus, et la résilience de la stratégie.',
      iroManagement: 'Gestion des impacts, risques et opportunités',
      iro1: 'IRO-1 – Description du processus d’identification et d’évaluation des impacts, risques et opportunités matériels',
      iro1Body:
        'La démarche d’analyse de double matérialité : sources, critères et seuils, parties prenantes consultées, validation par la gouvernance. C’est la première chose qu’examine le certificateur.',
      iro2: 'IRO-2 – Exigences de publication au titre des ESRS couvertes par l’état de durabilité de l’entreprise',
      iro2Body:
        'La liste des exigences de publication appliquées, avec le renvoi vers leur emplacement dans le rapport, et celles jugées non matérielles. L’index placé en annexe se construit à partir des références portées par chaque section.',
      environmental: 'Informations environnementales',
      climateTarget: 'Cible de réduction des émissions de gaz à effet de serre',
      taxonomy: 'Taxonomie européenne',
      taxonomyIntro: 'Activités durables au sens de la taxonomie',
      taxonomyIntroBody:
        'La part du chiffre d’affaires, des dépenses d’investissement (CapEx) et des dépenses d’exploitation (OpEx) éligibles puis alignées au titre de l’article 8 du règlement (UE) 2020/852, et la méthode de calcul retenue.',
      social: 'Informations sociales et sociétales',
      governanceInformation: 'Informations en matière de gouvernance',
      sustainabilityAnnexes: 'Annexes au rapport de durabilité',
      disclosureIndex: 'Index des exigences de publication',
      otherEuLegislation: 'Points de données issus d’autres actes législatifs de l’Union',
      otherEuLegislationBody:
        'La liste prévue à l’appendice B d’ESRS 2, indiquant pour chaque point de données s’il est publié et où il se trouve.',
      definitions: 'Définitions',
      certificationReport: 'Rapport de certification des informations en matière de durabilité',
      certificationReportBody:
        'Le rapport du commissaire aux comptes ou de l’organisme tiers indépendant, avec la conclusion formulée et le niveau d’assurance obtenu.',
      vigilance: 'Plan de vigilance',
      vigilanceIntro: 'Périmètre et démarche',
      vigilanceIntroBody:
        'Les sociétés couvertes, les fournisseurs et sous-traitants concernés, et la façon dont le plan a été élaboré avec les parties prenantes.',
      vigilanceMapping: 'Cartographie des risques',
      vigilanceHumanRights: 'Droits humains et libertés fondamentales',
      vigilanceHealthSafety: 'Santé et sécurité des personnes',
      vigilanceEnvironment: 'Environnement',
      vigilanceAssessment:
        'Procédures d’évaluation régulière des filiales, sous-traitants et fournisseurs',
      vigilanceAssessmentBody:
        'Comment la situation des filiales et des partenaires commerciaux est évaluée au regard de la cartographie, et à quelle fréquence.',
      vigilanceMitigation:
        'Actions d’atténuation des risques et de prévention des atteintes graves',
      vigilanceMitigationBody:
        'Les actions engagées pour chacun des risques prioritaires identifiés dans la cartographie.',
      vigilanceAlert: 'Mécanisme d’alerte et de recueil des signalements',
      vigilanceAlertBody:
        'Le dispositif ouvert aux salariés et aux tiers, établi en concertation avec les organisations syndicales représentatives, et le traitement des signalements reçus.',
      vigilanceMonitoring: 'Dispositif de suivi des mesures et d’évaluation de leur efficacité',
      vigilanceMonitoringBody:
        'Les indicateurs retenus pour suivre la mise en œuvre du plan et en mesurer l’efficacité.',
      vigilanceReport: 'Compte rendu de mise en œuvre',
      vigilanceReportBody:
        'Ce qui a été réalisé au cours de l’exercice, pour chacune des mesures du plan.',
      corporateGovernance: 'Rapport sur le gouvernement d’entreprise',
      boardComposition: 'Structure et composition de la gouvernance',
      boardMember: 'Membre',
      boardRole: 'Fonction',
      boardIndependent: 'Indépendant',
      boardCommittees: 'Comités',
      boardFunctioning: 'Fonctionnement des organes de gouvernance',
      boardFunctioningBody:
        'La fréquence des réunions, le taux d’assiduité, les travaux des comités et l’évaluation du conseil.',
      diversity: 'Politique de diversité',
      diversityBody:
        'La politique appliquée à la composition du conseil et des instances dirigeantes, ses objectifs et ses résultats, notamment en matière de représentation équilibrée des femmes et des hommes.',
      financialControl: 'Contrôle interne relatif à l’élaboration de l’information financière',
      financialControlBody:
        'Les principales caractéristiques des procédures de contrôle interne et de gestion des risques relatives à l’élaboration et au traitement de l’information comptable et financière.',
      consolidated: 'Comptes consolidés',
      auditorsConsolidated: 'Rapport des commissaires aux comptes sur les comptes consolidés',
      auditorsConsolidatedBody:
        'L’opinion, son fondement, la justification des appréciations et les points clés de l’audit.',
      comprehensiveIncome: 'État du résultat global',
      consolidatedBalanceSheet: 'Bilan consolidé',
      consolidatedCashFlows: 'Tableau des flux de trésorerie consolidés',
      changesInEquity: 'Tableau de variation des capitaux propres consolidés',
      equityItem: 'Mouvements',
      shareCapital: 'Capital',
      reserves: 'Réserves',
      netIncome: 'Résultat',
      totalEquity: 'Total des capitaux propres',
      consolidatedNotes: 'Annexe aux comptes consolidés',
      consolidatedNotesBody:
        'Le référentiel appliqué, les principes et méthodes comptables, les jugements et estimations significatifs, puis les notes relatives à chaque poste.',
      firstNote: 'Note 1',
      parent: 'Comptes sociaux',
      auditorsParent: 'Rapport des commissaires aux comptes sur les comptes annuels',
      auditorsParentBody:
        'L’opinion sur les comptes annuels de la société mère, son fondement et la justification des appréciations.',
      parentBalanceSheet: 'Bilan',
      parentIncomeStatement: 'Compte de résultat',
      parentNotes: 'Annexe aux comptes sociaux',
    },

    csrd: {
      name: 'D\u00e9claration de durabilit\u00e9',
      about:
        'La structure pos\u00e9e par les ESRS : informations g\u00e9n\u00e9rales, puis environnement, social et gouvernance.',

      general: 'ESRS 2 \u2013 Informations g\u00e9n\u00e9rales',
      basis: 'Base de pr\u00e9paration',
      basisBody:
        'Pr\u00e9cisez le p\u00e9rim\u00e8tre de la d\u00e9claration, sa consolidation, et si elle couvre la cha\u00eene de valeur amont et aval.',
      governance: 'Gouvernance',
      governanceBody:
        'D\u00e9crivez le r\u00f4le des organes d\u2019administration, de direction et de surveillance : composition, comp\u00e9tences en mati\u00e8re de durabilit\u00e9, information re\u00e7ue.',
      strategy: 'Strat\u00e9gie et mod\u00e8le d\u2019affaires',
      strategyBody:
        'Pr\u00e9sentez le mod\u00e8le d\u2019affaires, les march\u00e9s servis et la place de la durabilit\u00e9 dans la strat\u00e9gie.',
      stakeholders: 'Parties prenantes',
      stakeholdersBody:
        'Qui sont les parties prenantes, comment elles sont associ\u00e9es, et ce que leurs vues ont chang\u00e9 \u00e0 la strat\u00e9gie.',
      iros: 'Impacts, risques et opportunit\u00e9s',
      irosBody:
        'Les impacts, risques et opportunit\u00e9s mat\u00e9riels retenus, et leur interaction avec la strat\u00e9gie et le mod\u00e8le d\u2019affaires.',
      assessment: 'Analyse de double mat\u00e9rialit\u00e9',
      assessmentBody:
        'D\u00e9crivez le processus suivi : sources, seuils, parties prenantes consult\u00e9es, validation. C\u2019est ce processus qu\u2019un v\u00e9rificateur examine en premier.',
      covered: 'Informations couvertes',
      coveredBody:
        'La liste des exigences de publication couvertes, celles jug\u00e9es non mat\u00e9rielles et celles diff\u00e9r\u00e9es au titre des dispositions transitoires.',

      environment: 'Environnement',
      social: 'Social',
      governanceTopic: 'Gouvernance',
      annex: 'Annexes',

      topics: {
        e1: 'E1 \u2013 Changement climatique',
        e2: 'E2 \u2013 Pollution',
        e3: 'E3 \u2013 Ressources hydriques et marines',
        e4: 'E4 \u2013 Biodiversit\u00e9 et \u00e9cosyst\u00e8mes',
        e5: 'E5 \u2013 Utilisation des ressources et \u00e9conomie circulaire',
        s1: 'S1 \u2013 Personnel de l\u2019entreprise',
        s2: 'S2 \u2013 Travailleurs de la cha\u00eene de valeur',
        s3: 'S3 \u2013 Communaut\u00e9s affect\u00e9es',
        s4: 'S4 \u2013 Consommateurs et utilisateurs finaux',
        g1: 'G1 \u2013 Conduite des affaires',
      },

      climateTarget: 'Réduction des émissions',
      materialityTitle: 'Mat\u00e9rialit\u00e9',
      materialityBody:
        'Ce sujet est-il mat\u00e9riel, et pourquoi. Un sujet appr\u00e9ci\u00e9 et jug\u00e9 non mat\u00e9riel se dit : l\u2019omettre sans le dire n\u2019est pas une omission, c\u2019est un manquement. Marquez ensuite la section avec sa r\u00e9f\u00e9rence ESRS.',
      policies: 'Politiques',
      policiesBody:
        'Les politiques adopt\u00e9es, leur port\u00e9e, qui en r\u00e9pond et comment elles sont rendues publiques.',
      actions: 'Actions',
      actionsBody:
        'Les actions engag\u00e9es, les ressources qui leur sont affect\u00e9es et l\u2019horizon retenu.',
      targets: 'Cibles',
      targetsBody:
        'Les cibles chiffr\u00e9es, leur ann\u00e9e de r\u00e9f\u00e9rence, leur \u00e9ch\u00e9ance et l\u2019avancement constat\u00e9.',
      metrics: 'Indicateurs',

      assuranceTitle: 'Rapport d\u2019assurance',
      assuranceBody:
        'La conclusion du v\u00e9rificateur, son niveau d\u2019assurance et l\u2019\u00e9tendue de ses travaux.',
      definitions: 'D\u00e9finitions',
    },
  },

  workspace: {
    product: 'Constructeur de rapports',
    title: 'Rapports',
    subtitle: 'Tous les rapports auxquels vous avez accès, et où chacun en est.',
    create: 'Nouveau rapport',
    emptyTitle: 'Aucun rapport pour le moment',
    empty: 'Commencez-en un avec Nouveau rapport.',
    loading: 'Chargement de vos rapports...',
    deleteOne: 'Supprimer {name}',
    invitationPending: 'Invitation en attente',
    untitled: 'Sans titre',
  },

  access: {
    menu: 'Droits',
    title: 'Droits',
    subtitle:
      'Qui peut faire quoi, et pour quelle direction. Un changement s’applique à leur prochaine requête.',
    back: 'Retour aux rapports',
    notAllowed: 'Seul un administrateur peut modifier les droits.',
    addLabel: 'Ajouter une personne',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    edit: 'Modifier',
    editOne: 'Modifier {name}',
    save: 'Enregistrer',
    cancel: 'Annuler',
    addHint:
      'L’identifiant affiché par le portail pour le compte. Prénom, nom et e-mail sont facultatifs ici : tant que le portail ne dit pas qui est la personne, ce sont eux que les autres écrans affichent.',
    addPlaceholder: 'Par exemple 1058',
    add: 'Ajouter',
    empty: 'Personne n’a encore de profil. Ajoutez une personne par son identifiant.',
    tableCaption: 'Les personnes et leurs droits',
    person: 'Personne',
    profiles: 'Profils',
    directions: 'Directions',
    lastSeen: 'Dernière connexion',
    neverSeen: 'Pas encore connectée',
    grantedBy: 'Défini par {name}',
    subject: 'ID {sub}',
    actions: 'Actions',
    remove: 'Retirer tous les droits de {name}',
    removeShort: 'Retirer',
    level: {
      none: 'Aucun accès',
      read: 'Consulter',
      write: 'Modifier',
    },
    profile: {
      RFA_USER: 'Utilisateur',
      RFA_VALIDEUR: 'Valideur',
      RFA_ADMIN: 'Administrateur',
    },
  },

  csrdLink: {
    title: 'Lié à CSRD',
    locked:
      'Les chiffres et les libellés viennent de CSRD et ne se modifient pas ici. Modifiez-les dans CSRD, puis rafraîchissez. L’apparence du tableau reste à vous.',
    facts: {
      entity: 'Entité',
      reporting: 'Reporting',
      disclosure: 'Exigence',
      read: 'Dernière lecture',
      status: 'Statut CSRD',
    },
    status: 'Collecte {collected} · Contrôle {controlled} · Audit {audited}',
    statusHint:
      'Numérotation propre à CSRD de l’avancement de la collecte, de son contrôle et de son audit.',
    refresh: 'Rafraîchir depuis CSRD',
    state: {
      checking: 'Vérification auprès de CSRD...',
      current: 'À jour avec CSRD.',
      stale: '{count} cellule diffère de CSRD. | {count} cellules diffèrent de CSRD.',
      layout: 'La mise en page du tableau a changé dans CSRD.',
      refreshing: 'Lecture de CSRD...',
    },
    result: {
      nothing: 'Rien n’avait changé dans CSRD.',
      updated: '{count} cellule mise à jour, {date}. | {count} cellules mises à jour, {date}.',
      reshaped:
        'La mise en page a changé dans CSRD : le tableau a été recomposé. Sa mise en forme est conservée sur les lignes et colonnes encore présentes.',
      undo: 'Annuler rétablit les chiffres précédents.',
    },
    banner: {
      changed:
        'CSRD a changé depuis la lecture de ces chiffres ({date}) : {count} tableau concerné. | CSRD a changé depuis la lecture de ces chiffres ({date}) : {count} tableaux concernés.',
      refresh: 'Rafraîchir',
      later: 'Plus tard',
      blocked:
        'CSRD n’est pas sur le reporting principal de ce rapport ({code}) : ses tableaux ne peuvent pas être vérifiés.',
      switch: 'Passer sur {code} dans CSRD',
    },
    errors: {
      otherReporting:
        'CSRD est sur un autre reporting. Choisissez {code} dans CSRD, puis réessayez.',
      gone: 'CSRD n’a plus ce tableau.',
      detached:
        'Ce tableau vient du reporting {code}, que ce rapport n’a plus. Réimportez-le pour le rebrancher.',
      notEditable:
        'Ce rapport ne peut pas être modifié dans son état actuel : ses chiffres ne peuvent pas être rafraîchis.',
    },
  },

  session: {
    connecting: 'Redirection vers le portail...',
    blockedTitle: 'Connexion impossible',
    unreachableBody:
      'La connexion au portail n’a pas abouti. Vérifiez qu’il est démarré, puis relancez.',
    hostSilent: 'La page hôte n’a transmis aucune session. Rechargez-la, puis relancez.',
    retry: 'Relancer',
    callbackWorking: 'Connexion en cours...',
    callbackFailed: 'La connexion n’a pas pu aboutir. Fermez cet onglet et rouvrez l’application.',
    notFoundTitle: 'Rien ici',
    notFoundBody: 'Cette adresse ne correspond à rien dans l’application.',
    notFoundBack: 'Revenir à vos rapports',
  },

  bar: {
    reportName: 'Nom du rapport',
    backToReports: 'Revenir à vos rapports',
    saved: 'Enregistré',
    saving: 'Enregistrement',
    unsaved: 'Modifications non enregistrées',
    notSaved: 'Non enregistré',
    undo: 'Annuler',
    redo: 'Rétablir',
    comments: 'Commentaires',
    share: 'Partager',
    export: 'Exporter',
    history: 'Historique',
  },

  structure: {
    views: 'Vues du document',
    outline: 'Structure',
    contents: 'Sommaire',
    contentsEmpty: 'Aucun titre pour le moment. Les chapitres et les titres apparaîtront ici.',
    contentsPage: 'p. {page}',
    contentsPages: '{count} page | {count} pages',
    untitled: 'Sans titre',
    goTo: 'Aller à « {title} »',
  },

  selection: {
    title: '{count} élément sélectionné | {count} éléments sélectionnés',
    clear: 'Désélectionner',
    more: '{count} élément de plus | {count} éléments de plus',
    duplicate: 'Dupliquer',
    duplicateEnd: 'Dupliquer à la fin du document',
    moveStart: 'Déplacer au début',
    moveEnd: 'Déplacer à la fin',
    delete: 'Supprimer',
    hint: 'Ctrl ou Cmd et un clic ajoute une ligne, Maj et un clic prend tout ce qui est entre. Glissez l’un d’eux pour les déplacer tous. Une seule annulation remet tout en place.',
  },

  versions: {
    open: 'Versions',
    title: 'Versions de ce rapport',
    keepTitle: 'Garder cette version',
    namePlaceholder: 'Un nom (facultatif)',
    commentPlaceholder: 'Quelques mots sur cette version (facultatif)',
    keep: 'Garder cette version',
    keepHint:
      'Garde le contenu du rapport tel qu’il est, pour y revenir ou voir ce qui a changé depuis.',
    empty:
      'Aucune version gardée. Il en est aussi gardé une à chaque changement d’état du rapport.',
    number: 'V{number}',
    by: 'par {author}',
    auto: 'Automatique',
    locked: 'Verrouillée : prise une fois le rapport validé',
    reason: {
      manual: 'Version gardée',
      other: 'Version automatique',
      restore: 'Avant le retour à {from}',
      state: {
        brouillon: 'Rouvert',
        en_revue: 'Envoyé en revue',
        valide: 'Validé',
        diffuse: 'Diffusé',
        rejete: 'Rejeté',
      },
    },
    compare: 'Comparer',
    compareLatest: 'Comparer à la dernière version',
    restore: 'Restaurer',
    confirmRestore:
      'Restaurer V{number} ? Ce que dit le rapport maintenant est d’abord gardé comme une version.',
    confirm: 'Confirmer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    confirmDelete: 'Supprimer cette version ?',
    cannotRestore:
      'Un rapport en revue ou validé ne peut pas être modifié : rouvrez-le pour revenir en arrière.',
    bar: {
      title: 'Modifications',
      against: 'Comparé à V{number}, {date}',
      count: '{count} section modifiée | {count} sections modifiées',
      none: 'Rien n’a changé depuis cette version.',
      list: 'Liste',
      previous: 'Modification précédente',
      next: 'Modification suivante',
      quit: 'Arrêter la comparaison',
      export: 'Exporter en PDF',
      exporting: 'Export en cours',
      exportFailed: 'Les modifications n’ont pas pu être exportées.',
    },
    review: {
      accept: 'Accepter cette modification',
      reject: 'Rejeter cette modification',
      rejectRemoval: 'Rejeter : ramener le bloc',
      acceptAll: 'Tout accepter',
      accepted: 'acceptée',
      rejected: 'rejetée',
      acceptedBy: 'Acceptée par {author}, {when}',
      rejectedBy: 'Rejetée par {author}, {when}',
      toRead: '{count} à relire',
      allRead: 'Tout a été relu.',
      unread:
        '{count} modification depuis la V{number} n’a pas été relue. | {count} modifications depuis la V{number} n’ont pas été relues.',
    },
    delta: {
      title: 'Modifications de {report}',
      version: 'V{number}, {date}',
      against: 'Comparé à {against}',
      made: 'Établi le {date} par {author}',
      nothing: 'Rien n’a changé depuis cette version.',
      modified: 'section modifiée | sections modifiées',
      added: 'section ajoutée | sections ajoutées',
      removed: 'section supprimée | sections supprimées',
      moved: 'section déplacée | sections déplacées',
    },
    change: {
      added: 'Ajouté',
      modified: 'Modifié',
      moved: 'Déplacé',
      removed: 'Supprimé',
    },
    list: {
      title: 'Ce qui a changé depuis V{number}',
      removedHint: 'Supprimé depuis cette version',
      empty: 'Rien n’a changé depuis cette version.',
    },
    cell: {
      title: 'Valeur modifiée depuis V{number}',
      titleLabel: 'Libellé modifié depuis V{number}',
      titleNew: 'Nouveau depuis V{number}',
      source: 'CSRD',
      was: 'Dans V{number}',
      now: 'Actuelle',
      gap: 'Écart',
      read: 'Lu dans CSRD le',
      where: 'Provenance',
      history: 'Voir l’historique dans CSRD',
      last: 'Dernière modification',
      open: 'Cliquer pour voir l’historique de la cellule',
      notes:
        '{count} valeur modifiée depuis V{number} | {count} valeurs modifiées depuis V{number}',
    },
    cellPanel: {
      title: 'Historique de la cellule',
      cell: 'Cellule',
      header: 'En-tête',
      was: 'Ancienne valeur',
      after: 'Nouvelle valeur',
      afterVersion: 'Après V{number}',
      gone: 'absente',
      empty: 'Aucune modification de cette cellule dans le journal.',
    },
    detail: {
      title: 'Modifications de ce bloc',
      before: 'Avant : V{number}',
      after: 'Après : le rapport maintenant',
      sideBySide: 'Côte à côte',
      merged: 'Rédaction fusionnée',
      wordsAdded: '+{count} mots',
      wordsRemoved: '-{count} mots',
      paragraphs: '{touched} paragraphes touchés sur {total}',
      absent: 'Ce paragraphe n’est pas dans cette version',
      added: 'Ajouté',
      removed: 'Supprimé',
      cells: '{count} cellule modifiée | {count} cellules modifiées',
      rowsAdded: '{count} ligne ajoutée | {count} lignes ajoutées',
      rowsRemoved: '{count} ligne supprimée | {count} lignes supprimées',
      fields: 'Ce qui est réglé autrement',
      wasEmpty: 'rien',
      addedBlock: 'Ce bloc a été ajouté après cette version.',
      removedBlock: 'Ce bloc était dans cette version et n’est plus dans le rapport.',
      movedBlock: 'Ce bloc dit la même chose, à un autre endroit.',
      unchanged: 'Rien n’a changé dans ce bloc.',
    },
  },

  journal: {
    tabs: { versions: 'Versions', journal: 'Qui a modifié quoi' },
    trail: {
      empty: 'Rien n’a été modifié depuis la création du rapport.',
      more: 'Voir les modifications plus anciennes',
    },
    history: {
      open: 'Historique de ce bloc',
      title: 'Historique du bloc',
      latest: 'Dernière',
      restore: 'retour à une version',
      empty: 'Personne n’a modifié ce bloc depuis la création du rapport.',
      comparedTo: 'Comparé à',
      previous: 'La modification précédente',
      currentReport: 'Le rapport maintenant',
      before: 'Avant : {author}, {when}',
      after: 'Après : {author}, {when}',
      beforeAdded: 'Avant : ce bloc n’existait pas',
      original: 'Avant : le bloc tel qu’il était avant cette modification',
      noEarlier: 'Aucun état plus ancien de ce bloc n’est gardé',
      saidThen: 'Ce qu’il disait, {when}',
      now: 'Le rapport maintenant',
      gone: 'Le bloc n’existe plus',
      putBack: 'Remettre le bloc dans l’état d’après cette modification',
      putBackBefore: 'Remettre le bloc dans l’état d’avant cette modification',
      bringBack: 'Ramener le bloc dans l’état d’après cette modification',
      replaced: 'Fait. Le bloc est revenu à cet état ; annuler rend ce qu’il disait avant.',
      returned: 'Fait. Le bloc est de retour à la fin du rapport ; annuler le retire de nouveau.',
      noContent:
        'Ce que disait le bloc n’est pas gardé : il était trop volumineux pour être copié à chaque modification.',
    },
  },

  outline: {
    sections: 'Sections',
    reportOutline: 'Structure du rapport',
    empty: 'Rien pour le moment. Ajoutez une section depuis le panneau de droite.',
    expand: 'Déplier',
    collapse: 'Replier',
    openCount: '{count} en cours',
  },

  palette: {
    title: 'Ajouter',
    dragHint:
      'Cliquez pour ajouter après la sélection, ou glissez sur la page pour choisir l’endroit.',
    addSection: 'Ajouter une section',
    importFrom: 'Reprendre un document',
    word: 'Word',
    excel: 'Excel',
    pdf: 'PDF',
    pdfHint:
      'Reprendre les pages d’un rapport PDF en sections modifiables, ou exactement comme imprimées.',
    wordHint:
      'Reprendre les titres, les paragraphes et les tableaux d’un document Word, en choisissant lesquels.',
    excelHint: 'Reprendre une plage de cellules d’un classeur, sous forme de tableau.',
    csrd: 'CSRD',
    csrdHint: 'Reprendre les exigences collectées dans CSRD, avec leurs textes et leurs tableaux.',
    bands: {
      structure: 'Structure',
      content: 'Contenu',
      annex: 'Annexe',
      sustainability: 'Durabilité',
      frontAndBack: 'Début et fin',
    },
    hints: {
      group: 'Un chapitre qui contient d’autres sections',
      heading: 'Un titre dans le document',
      pageBreak: 'Commencer la suite sur une nouvelle page',
      layer: 'Des textes, des images et des formes posés librement sur la page',
      imagePage: 'Une image seule, sur sa propre page',
      fixedPage: 'Une page importée d’un PDF, exactement comme imprimée',
      text: 'Du texte, avec sa mise en forme',
      table: 'Des lignes et des colonnes de montants',
      kpi: 'Une série de chiffres clés',
      image: 'Une image ou un graphique',
      cover: 'La page de garde',
      contents: 'Généré à partir des titres',
      chart: 'Un graphique tracé à partir de chiffres saisis',
      note: 'Une note numérotée de l’annexe',
      scope: 'Les entités consolidées et leur méthode',
      glossary: 'Des termes et leur définition',
      divider: 'Un filet, ou une respiration',
      datapoint: 'Un chiffre publié, avec son unité, son comparatif et sa méthode',
      target: 'Une cible chiffrée, et l’avancement qui s’en déduit',
      materiality: 'La matrice de double matérialité',
      iro: 'Le tableau des impacts, risques et opportunités',
      risks: 'Les principaux risques, leur description et leur gestion',
      ghg: 'Les scopes 1, 2 et 3, avec des sous-totaux qui ne peuvent pas \u00eatre saisis',
      taxonomy: 'Chiffre d\u2019affaires, CapEx et OpEx \u00e9ligibles et align\u00e9s',
      disclosureIndex:
        'L\u2019index construit \u00e0 partir des r\u00e9f\u00e9rences ESRS du document',
      signatures: 'Qui signe, et où',
    },
  },

  sections: {
    cover: 'Page de garde',
    group: 'Groupe',
    heading: 'Titre',
    text: 'Texte',
    table: 'Tableau',
    image: 'Image',
    kpi: 'Chiffres clés',
    chart: 'Graphique',
    note: 'Note',
    scope: 'Périmètre',
    glossary: 'Glossaire',
    divider: 'Séparateur',
    datapoint: 'Donnée',
    target: 'Cible',
    materiality: 'Matérialité',
    iro: 'IRO',
    risks: 'Risques',
    ghg: 'Bilan GES',
    taxonomy: 'Taxonomie',
    disclosureIndex: 'Index ESRS',
    contents: 'Sommaire',
    signatures: 'Signatures',
    pageBreak: 'Saut de page',
    layer: 'Placement libre',
    imagePage: 'Image pleine page',
    fixedPage: 'Page importée',
  },

  /**
   * What a new section is called when it is created.
   *
   * Content, not chrome: it is written into the document and printed. Kept
   * apart from `sections` above, which names the *kind* in the interface, so
   * "Titre" can label a palette button while a new one is called "Nouveau
   * titre" on the page.
   */
  newSection: {
    cover: 'Page de garde',
    group: 'Nouveau groupe',
    heading: 'Nouveau titre',
    text: 'Texte',
    table: 'Nouveau tableau',
    image: 'Image',
    kpi: 'Chiffres clés',
    contents: 'Sommaire',
    signatures: 'Signatures',
    pageBreak: 'Saut de page',
    layer: 'Placement libre',
    imagePage: 'Image pleine page',
    fixedPage: 'Page importée',
    chart: 'Graphique',
    note: 'Nouvelle note',
    scope: 'Périmètre de consolidation',
    glossary: 'Glossaire',
    divider: 'Séparateur',
    datapoint: 'Nouvelle donnée',
    target: 'Nouvelle cible',
    materiality: 'Analyse de double matérialité',
    iro: 'Impacts, risques et opportunités',
    risks: 'Facteurs de risques',
    ghg: 'Bilan des \u00e9missions de GES',
    taxonomy: 'Taxonomie europ\u00e9enne',
    disclosureIndex: 'Index des informations publi\u00e9es',
    firstChapter: 'Chapitre 1',
  },

  devAccount: {
    title: 'Agir comme un autre compte (développement)',
    hint: 'Développement seulement : remplace la connexion. Choisissez qui agit dans cet onglet.',
    fixed: 'Utilisateur de développement',
    none: 'Aucun compte à proposer. Ajoutez des personnes dans l’écran Accès, avec un token dans le dossier des secrets.',
    expired: 'token expiré',
  },

  canvas: {
    empty:
      'Un rapport vide. Ajoutez une section depuis le panneau de droite, ou importez-en une depuis Word ou Excel.',
    groupEmpty:
      'Ce groupe est vide. Déposez-y des sections dans la structure, ou ajoutez-en une pendant qu’il est sélectionné.',
    masked: 'Contenu restreint',
    zoom: 'Zoom',
    zoomIn: 'Agrandir',
    zoomOut: 'Réduire',
    view: 'Affichage des pages',
    viewSheets: 'Pages séparées',
    viewContinuous: 'Défilement continu',
    blankSheet: 'Page blanche',
    fold: 'Suite page suivante',
    spread: 'Double page',
    layer: {
      none: 'aucun élément',
      one: '1 élément',
      many: '{count} éléments',
      textEmpty: 'Texte',
      imageEmpty: 'Image',
      handle: 'Redimensionner',
      turn: 'Faire pivoter : Maj pour tourner par 15°',
      drop: 'Déposez l’image pour la placer ici',
      dropRefused: {
        notAnImage: 'Ce fichier n’est pas une image.',
        tooLarge: 'Cette image dépasse 4 Mo.',
        unreadable: 'Cette image n’a pas pu être lue.',
      },
    },
    foldHint:
      'Ce qui dépasse cette ligne s’imprime sur une page de plus. Un saut de page permet de choisir où elle commence.',
    comment: 'Commenter cette section',
    openDiscussion: 'Ouvrir la discussion sur cette section',
    dragToMove: 'Glisser pour déplacer',
    emptyText: 'Vide',
    noImage: 'Aucune image choisie',
    contentsEmpty: 'Rien à lister pour l’instant. Les titres et les groupes apparaissent ici.',
    defaults: {
      datapoint: 'Donnée publiée',
      target: 'Cible',
      subtitle: 'Comptes annuels consolid\u00e9s',
      standard: 'R\u00e9f\u00e9rentiel comptable',
      note: 'Mention de pied : r\u00e9f\u00e9rence, version, confidentialit\u00e9',
      logo: 'Votre logo',
      framework: 'IFRS',
      assurance: 'Assurance limit\u00e9e',
      entity: 'Entité',
      period: 'Période',
      name: 'Nom',
    },
  },

  editor: {
    placeholder: 'Écrivez ici',
    formatting: 'Mise en forme',
    bold: 'Gras',
    italic: 'Italique',
    strike: 'Barré',
    underline: 'Souligné',
    superscript: 'Exposant',
    subscript: 'Indice',
    plain: 'Effacer la mise en forme',
    bulletList: 'Liste à puces',
    orderedList: 'Liste numérotée',
    link: 'Lien',
    linkPrompt: 'Adresse du lien',
    textStyle: 'Style de texte',
    textStyleNone: 'Aucun style',
  },

  document: {
    custom: 'Personnalisé',
    amount: 'Valeur',
    width: 'Largeur',
    height: 'Hauteur',
    marginTop: 'Haut',
    marginSide: 'Côtés',
    marginBottom: 'Bas',
    bleed: 'Fond perdu',
    bleedHint:
      'De combien les images de fond débordent du format fini, pour qu’un massicot légèrement décalé ne laisse pas de liseré blanc. Ce qui déborde est perdu : n’y placez rien à lire.',
    bleeds: {
      none: 'Aucun',
      narrow: '3 mm',
      wide: '5 mm',
    },
    // ---------- The kind of dossier ----------
    tables: 'Tableaux',
    tableHead: 'En-tête',
    tableHeadHint:
      'Comment les noms des colonnes sont dessinés, dans chaque tableau qui ne règle pas le sien.',
    tableRule: 'Filets',
    tableRuleHint:
      'Un \u00e9tat imprim\u00e9 se r\u00e8gle en filets horizontaux : l\u2019\u0153il suit une ligne, il ne traverse pas une colonne.',
    tableRules: {
      all: 'Quadrill\u00e9',
      horizontal: 'Horizontaux',
      minimal: 'Au minimum',
    },
    tableDensity: 'Interligne',
    tableDensityHint:
      'De quoi respirer sur un \u00e9tat de soixante lignes, ou de quoi le faire tenir.',
    tableDensities: {
      compact: 'Serr\u00e9',
      normal: 'Normal',
      roomy: 'A\u00e9r\u00e9',
    },
    tableZebra: 'Lignes altern\u00e9es',
    tableZebraHint:
      'Une teinte tr\u00e8s l\u00e9g\u00e8re une ligne sur deux, pour un tableau trop large pour \u00eatre suivi.',
    negativesInRed: 'N\u00e9gatifs en rouge',
    negativesInRedHint:
      'La convention reste le noir et les parenth\u00e8ses : le rouge ne survit pas \u00e0 une photocopie.',

    structure: 'Structure',
    headingNumbering: 'Num\u00e9rotation',
    headingNumberingHint:
      'Les titres et les chapitres, num\u00e9rot\u00e9s dans le document comme dans le sommaire.',
    headingNumbers: {
      none: 'Aucune',
      decimal: '1, 1.1',
      legal: 'I, A, 1',
    },
    chapterPageBreak: 'Chapitre sur une nouvelle page',
    chapterPageBreakHint:
      'A l\u2019impression seulement : la page \u00e0 l\u2019\u00e9cran est continue.',
    contentsLeaders: 'Points de conduite',
    contentsLeadersHint:
      'Les points qui m\u00e8nent du titre \u00e0 son num\u00e9ro de page dans le sommaire.',

    dossier: 'Dossier financier',
    dossierHint:
      'Ce que le document dit de lui-m\u00eame. Ces mentions sont reprises sur la page de garde et au-dessus des tableaux, et doivent donc \u00eatre dites une seule fois.',
    framework: 'R\u00e9f\u00e9rentiel',
    frameworkHint:
      'Le r\u00e9f\u00e9rentiel comptable des \u00e9tats. Repris sous le titre de la page de garde.',
    frameworks: {
      none: 'Non pr\u00e9cis\u00e9',
    },
    currency: 'Monnaie',
    currencyHint:
      'La monnaie des chiffres. Un lecteur fran\u00e7ais d\u2019une filiale tunisienne voit toujours des dinars.',
    currencies: {
      none: 'Aucune',
    },
    figureScale: 'Unit\u00e9',
    figureScaleHint:
      'Indiqu\u00e9e au-dessus de chaque tableau. Aucun chiffre n\u2019est recalcul\u00e9 : c\u2019est une mention, pas une conversion.',
    figureScales: {
      none: 'Non indiqu\u00e9e',
      units: 'Unit\u00e9s',
      thousands: 'Milliers',
      millions: 'Millions',
    },
    scales: {
      units: 'En {currency}',
      thousands: 'En milliers de {currency}',
      millions: 'En millions de {currency}',
    },
    scalesBare: {
      units: 'En unit\u00e9s',
      thousands: 'En milliers',
      millions: 'En millions',
    },

    sustainability: 'Durabilit\u00e9 (CSRD / ESG)',
    sustainabilityHint:
      'Un m\u00eame volume peut relever d\u2019un r\u00e9f\u00e9rentiel comptable et d\u2019un r\u00e9f\u00e9rentiel de durabilit\u00e9. Les deux se disent donc s\u00e9par\u00e9ment.',
    esgFramework: 'R\u00e9f\u00e9rentiel de durabilit\u00e9',
    esgFrameworkHint: 'Repris sur la page de garde, avec le niveau d\u2019assurance.',
    assurance: 'Assurance',
    assuranceHint:
      'Ce qu\u2019un auditeur en a dit. Rien n\u2019est imprim\u00e9 tant que ce n\u2019est pas pr\u00e9cis\u00e9.',
    assurances: {
      none: 'Aucune',
      limited: 'Assurance limit\u00e9e',
      reasonable: 'Assurance raisonnable',
    },

    houseStyle: 'Charte graphique',
    houseStyleHint:
      'Couleurs, polices, tableaux et graphiques réglés ensemble, essayés sur un aperçu puis appliqués d’un coup. Enregistrée, une charte se retrouve dans vos prochains rapports.',
    houseStyleOpen: 'Ouvrir la charte graphique',
    tableStyles: 'Styles de tableau',
    tableStylesHint:
      'La bibliothèque des façons de dessiner un tableau, partagée dans la firme. On les crée et on les modifie ici, puis on en choisit une sur chaque tableau au lieu de régler chaque tableau à la main.',
    tableStylesOpen: 'Ouvrir les styles de tableau',
    specimenFigures: {
      revenue: 'Chiffre d’affaires, M€',
      result: 'Résultat net, M€',
      emissions: 'Émissions par voyageur',
    },
    specimenChart: {
      title: 'Chiffre d’affaires par activité',
      transport: 'Transport',
      subsidiaries: 'Filiales',
      services: 'Services',
    },
    specimen: 'Sp\u00e9cimen des r\u00e9glages',
    specimenChapter: 'Comptes consolid\u00e9s',
    specimenSection: 'Actif courant',
    specimenBody:
      'Ce feuillet n\u2019appartient pas au rapport. Il rassemble un exemple de chaque chose que ces r\u00e9glages commandent, parce qu\u2019une page de garde ne comporte ni tableau, ni titre num\u00e9rot\u00e9, ni sommaire.',
    specimenContents: 'Sommaire',
    specimenContentsLines: {
      one: 'Rapport de gestion',
    },
    specimenTable: {
      item: 'Poste',
      cash: 'Tr\u00e9sorerie',
      debtors: 'Cr\u00e9ances clients',
      provisions: 'Provisions',
      total: 'Total actif courant',
    },

    title: 'Mise en forme',
    fromHere: 'La mise en forme du rapport, son papier et sa typographie.',
    open: 'Ouvrir la mise en forme',
    backToReport: 'Revenir au rapport',
    preview: 'Aperçu',
    previewEmpty: 'Rien à prévisualiser pour l’instant.',
    paper: 'Papier',
    pageSize: 'Format',
    pageSizeHint: 'Le papier sur lequel le rapport est imprimé.',
    margin: 'Marges',
    marginHint: 'La blancheur autour du texte.',
    text: 'Texte',
    textSize: 'Corps',
    textSizeHint: 'La taille du texte courant, en points.',
    alignment: 'Alignement',
    alignmentHint: 'Justifié comme un rapport imprimé, ou en drapeau si la césure ne convient pas.',
    colour: 'Couleurs',
    paperColour: 'Papier',
    paperColourHint: 'Blanc, ou crème comme un rapport annuel imprimé.',
    accent: 'Couleur d’accent',
    accentHint: 'Les filets du document : page de garde, en-têtes de tableaux, chiffres clés.',
    accentPick: 'Choisir une couleur',
    accentHex: 'Couleur en hexadécimal',
    accentInvalid: 'Six chiffres hexadécimaux, par exemple 1c3f6e.',
    accentHeadings: 'Titres en couleur',
    accentHeadingsHint: 'Applique la couleur d’accent aux titres du document.',
    papers: {
      white: 'Blanc',
      cream: 'Crème',
    },
    accents: {
      ink: 'Encre',
      navy: 'Marine',
      teal: 'Sarcelle',
      forest: 'Forêt',
      burgundy: 'Bordeaux',
      bronze: 'Bronze',
    },
    typeface: 'Typographie',
    typefaceHint: 'La police dans laquelle le rapport est composé et exporté.',
    sample: 'Résultat opérationnel',
    storedWithReport:
      'Enregistrée avec le rapport, pas avec votre navigateur : tout le monde voit le même document, et le PDF correspond à l’écran.',
    pageSizes: {
      a4: 'A4',
      letter: 'Letter',
    },
    margins: {
      narrow: 'Étroites',
      normal: 'Normales',
      wide: 'Larges',
    },
    textSizes: {
      small: 'Petit',
      normal: 'Normal',
      large: 'Grand',
    },
    alignments: {
      justified: 'Justifié',
      ragged: 'En drapeau',
    },
    kinds: {
      sans: 'sans empattement',
      serif: 'avec empattements',
    },
    typefaces: {
      inter: 'Inter',
      'source-serif': 'Source Serif',
      lora: 'Lora',
      'plex-sans': 'IBM Plex Sans',
      garamond: 'EB Garamond',
    },
    textStylesGroup: 'Styles de texte',
    textStylesHint:
      'Une couleur et une police, sous un nom, à appliquer à une sélection depuis la barre d’outils du texte. Pas du CSS libre : uniquement une couleur et une police, pour les mêmes raisons que la couleur d’accent ci-dessus.',
    textStyles: {
      empty: 'Aucun style pour l’instant.',
      name: 'Nom du style',
      typeface: 'Police du style',
      remove: 'Supprimer',
      add: '+ Ajouter un style',
      sample: 'Exemple de texte',
      newName: 'Style {count}',
    },
  },

  fontLibrary: {
    title: 'Polices',
    open: 'Ajouter des polices',
    openHint: 'Google Fonts ou vos fichiers',
    missing: 'Police introuvable',
    sample: 'Texte d’aperçu',
    sampleDefault: 'Rapport annuel 2026 : 1 284 605 €',
    tabs: {
      library: 'Bibliothèque',
      google: 'Google Fonts',
      upload: 'Importer',
    },
    sources: {
      google: 'Google Fonts',
      upload: 'Importée',
    },
    categories: {
      'sans-serif': 'Sans empattement',
      serif: 'Avec empattements',
      display: 'Fantaisie',
      handwriting: 'Manuscrite',
      monospace: 'Chasse fixe',
    },
    allCategories: 'Toutes',
    weights: {
      100: 'Extra-fin',
      200: 'Ultra-léger',
      300: 'Léger',
      400: 'Normal',
      500: 'Médium',
      600: 'Demi-gras',
      700: 'Gras',
      800: 'Extra-gras',
      900: 'Noir',
    },
    weightRange: '{from} à {to}',
    italicOf: '{weight} italique',
    styles: '{count} variante | {count} variantes',
    library: {
      empty:
        'La bibliothèque est vide. Ajoutez une famille depuis Google Fonts, ou importez les fichiers de la police de votre charte.',
      browse: 'Parcourir Google Fonts',
      by: 'ajoutée par {owner}',
      rename: 'Renommer',
      renameSave: 'Enregistrer',
      addStyles: 'Ajouter des styles',
      remove: 'Supprimer la famille « {name} »',
      removeHint: 'Les rapports qui l’utilisent passeront à la police par défaut.',
      removeYes: 'Supprimer',
      removeNo: 'Garder',
    },
    google: {
      search: 'Rechercher une famille',
      kind: 'Genre',
      note: 'Une famille ajoutée est téléchargée une fois par le serveur, puis servie par l’application : les navigateurs ne contactent jamais Google. Toutes sont sous licence libre (OFL ou Apache) et peuvent être intégrées aux PDF.',
      results: '{count} famille | {count} familles',
      none: 'Aucune famille ne correspond à cette recherche.',
      more: 'Afficher plus',
      add: 'Ajouter',
      adding: 'Ajout…',
      added: 'Dans la bibliothèque',
    },
    upload: {
      drop: 'Déposez ici les fichiers d’une famille, ou',
      choose: 'choisissez-les',
      accepted:
        'WOFF2, WOFF, TTF ou OTF, 6 Mo au plus par fichier. Les fichiers d’une même famille (normal, gras, italique…) en forment une seule.',
      wrongKind: '{name} n’est pas un fichier de police.',
      tooLarge: '{name} dépasse 6 Mo.',
      remove: 'Retirer {name}',
      size: '{size} ko',
      name: 'Nom de la famille',
      namePlaceholder: 'Lu dans le premier fichier',
      category: 'Genre',
      licence:
        'Vérifiez que la licence de la police permet de l’intégrer dans des documents PDF et des pages web : les polices d’un rapport voyagent dans chaque export. Une police dont la licence l’interdit est refusée.',
      submit: 'Importer la famille',
    },
  },

  tableStyles: {
    title: 'Styles de tableau',
    new: '+ Nouveau style',
    empty: 'Aucun style de tableau. Créez-en un, puis choisissez-le sur un tableau.',
    default: 'Par défaut',
    usage: '{count} tableau | {count} tableau | {count} tableaux',
    name: 'Nom',
    notMine: 'Enregistré par {owner} : vous pouvez le consulter et le dupliquer, pas le modifier.',
    sample: 'Exemple',
    changeNote:
      'Enregistrer modifie le {count} tableau qui le suit. | Enregistrer modifie les {count} tableaux qui le suivent.',
    duplicate: 'Dupliquer',
    setDefault: 'Définir par défaut',
    unsetDefault: 'Ne plus le mettre par défaut',
    delete: 'Supprimer',
    confirmDelete: 'Confirmer la suppression',
    save: 'Enregistrer le style',
    pick: 'Choisissez un style dans la liste, ou créez-en un.',
    newName: 'Nouveau style',
    copyName: '{name} (copie)',
  },
  houseStyle: {
    title: 'Charte graphique',
    settings: 'Réglages de la charte',
    preview: 'Aperçu',
    previewNote: 'Le rapport ne change qu’une fois la charte appliquée.',
    firstPage: 'Première page du rapport',
    tabs: {
      colours: 'Couleurs',
      type: 'Typographie',
      tables: 'Tableaux',
      charts: 'Graphiques',
    },
    gallery: {
      label: 'Chartes',
      report: 'Ce rapport',
      current: 'Style actuel',
      currentNote: 'Le rapport tel qu’il est mis en forme aujourd’hui.',
      mine: 'Mes chartes',
      none: 'Enregistrez une charte pour la retrouver dans vos prochains rapports.',
      loading: 'Chargement des chartes…',
      shared: 'Chartes partagées',
      presets: 'Modèles',
      savedOn: 'Enregistrée le {when}',
      by: 'Par {owner}',
      remove: 'Supprimer la charte « {name} »',
      removeYes: 'Supprimer',
      removeNo: 'Garder',
    },
    presets: {
      classic: { name: 'Classique', note: 'Encre sombre, Inter, filets horizontaux.' },
      institutional: {
        name: 'Institutionnel',
        note: 'Marine, titres à empattements, en-têtes pleins.',
      },
      editorial: { name: 'Éditorial', note: 'Papier crème, bordeaux, tableaux à peine filetés.' },
      modern: { name: 'Moderne', note: 'Sarcelle, texte en drapeau, lignes alternées.' },
      sustainability: {
        name: 'Durabilité',
        note: 'Vert forêt, titres en Lora, graphiques dans les verts.',
      },
      sober: { name: 'Sobre', note: 'Noir sur blanc, quadrillé, serré : fait pour la photocopie.' },
    },
    colours: {
      paper: 'Papier',
      paperHint: 'Blanc, ou crème comme un rapport annuel imprimé.',
      accent: 'Couleur principale',
      accentHint:
        'Les filets, les en-têtes de tableaux, les chiffres clés et, si vous le voulez, les titres.',
      palette: 'Couleurs de la charte',
      paletteHint:
        'Proposées dans la palette du texte et, si vous le choisissez, dans les graphiques. Jusqu’à {max} en plus de la couleur principale.',
      main: 'Couleur principale',
      colour: 'Couleur {n}',
      hex: 'Couleur {n} en hexadécimal',
      remove: 'Retirer cette couleur',
      add: '+ Ajouter une couleur',
      suggest: 'Suggérer à partir de la couleur principale',
      headings: 'Titres',
      accentHeadings: 'Titres dans la couleur principale',
    },
    type: {
      body: 'Police du texte',
      bodyHint: 'Celle dans laquelle le rapport est composé et exporté.',
      heading: 'Police des titres',
      headingHint:
        'Des titres à empattements sur un texte sans, ou l’inverse : l’association la plus courante d’une charte.',
      sameAsBody: 'Comme le texte',
      headingSample: 'Rapport annuel',
      size: 'Taille du texte',
      align: 'Alignement',
      textStyles:
        '{count} style de texte du rapport est enregistré avec la charte. | {count} styles de texte du rapport sont enregistrés avec la charte.',
    },
    tables: {
      head: 'En-tête des tableaux',
      headHint:
        'Comment les noms des colonnes sont dessinés. Un tableau réglé à part garde le sien.',
      more: 'Lecture',
    },
    charts: {
      colours: 'Couleurs des graphiques',
      coloursHint: 'Une série à laquelle vous avez donné sa propre couleur la garde.',
      shades: 'Nuances de la couleur principale',
      shadesNote: 'Une seule couleur, lisible en noir et blanc.',
      palette: 'Couleurs de la charte',
      paletteNote: 'La couleur principale, puis les autres, dans l’ordre.',
      paletteEmpty: 'Ajoutez d’abord des couleurs à la charte, dans l’onglet Couleurs.',
    },
    save: {
      name: 'Nom de la charte',
      placeholder: 'Nom de la charte',
      saveNew: 'Enregistrer la charte',
      saveCopy: 'Enregistrer comme nouvelle',
      update: 'Mettre à jour',
      saved: 'Charte enregistrée : elle vous attend dans vos prochains rapports.',
      updated: 'Charte mise à jour.',
    },
    unchanged: 'Le rapport a déjà ce style.',
    changes: '{count} réglage du rapport changera | {count} réglages du rapport changeront',
    locked: 'Rapport en lecture seule : la charte peut être enregistrée, pas appliquée.',
    apply: 'Appliquer au rapport',
    start: {
      label: 'Charte graphique',
      hint: 'Le style dans lequel le rapport commence. Le dernier choisi est retenu pour le suivant.',
      none: 'Aucune (style par défaut)',
    },
  },

  methods: {
    full: 'Intégration globale',
    proportionate: 'Intégration proportionnelle',
    equity: 'Mise en équivalence',
  },

  sectionFields: {
    scope: {
      entity: 'Entité',
      country: 'Pays',
      method: 'Méthode',
      control: '% contrôle',
      interest: '% intérêt',
    },
  },

  esrs: {
    // The standards, by their official titles. The codes themselves are never
    // translated: E1 is E1 in every language.
    standards: {
      ESRS2: 'Informations g\u00e9n\u00e9rales',
      E1: 'Changement climatique',
      E2: 'Pollution',
      E3: 'Ressources hydriques et marines',
      E4: 'Biodiversit\u00e9 et \u00e9cosyst\u00e8mes',
      E5: 'Utilisation des ressources et \u00e9conomie circulaire',
      S1: 'Personnel de l\u2019entreprise',
      S2: 'Travailleurs de la cha\u00eene de valeur',
      S3: 'Communaut\u00e9s affect\u00e9es',
      S4: 'Consommateurs et utilisateurs finaux',
      G1: 'Conduite des affaires',
    },
    pillars: {
      general: 'G\u00e9n\u00e9ral',
      environment: 'Environnement',
      social: 'Social',
      governance: 'Gouvernance',
    },
    materialities: {
      material: 'Mat\u00e9riel',
      notMaterial: 'Non mat\u00e9riel',
      phasedIn: 'Diff\u00e9r\u00e9',
      byReference: 'Par renvoi',
    },
    materialityHints: {
      material: 'Sujet mat\u00e9riel, dont l\u2019information est publi\u00e9e ici.',
      notMaterial:
        'Sujet appr\u00e9ci\u00e9 et jug\u00e9 non mat\u00e9riel. C\u2019est une information \u00e0 part enti\u00e8re : l\u2019omettre sans le dire ne l\u2019est pas.',
      phasedIn: 'Publication diff\u00e9r\u00e9e au titre des dispositions transitoires.',
      byReference: 'Publi\u00e9 ailleurs dans le rapport, par renvoi.',
    },
  },

  inspector: {
    layer: {
      note: 'Les éléments se posent n’importe où sur la page, même au-delà du fond perdu. Les bords, le fond perdu, les marges, le milieu et les autres éléments font aimant : maintenez Alt pour passer outre. Maj et un clic en sélectionnent plusieurs, et une image se dépose directement sur la page.',
      add: 'Ajouter',
      adding: {
        text: 'Texte',
        image: 'Image',
        chart: 'Graphique',
        rectangle: 'Rectangle',
        ellipse: 'Ellipse',
        line: 'Ligne',
      },
      magnets: 'Aimants',
      magnetsHint: 'Bords de page, fond perdu, marges, milieu et autres éléments.',
      none: 'Aucun élément sur cette page pour l’instant.',
      tabs: {
        label: 'Placement libre',
        add: 'Ajouter',
        layers: 'Calques',
        element: 'Élément',
        arrange: 'Position',
      },
      addOne: 'Ajouter un élément',
      showLayers: 'Voir les calques',
      nothingTitle: 'Aucun élément sélectionné',
      nothingText: 'Cliquez un élément sur la page, ou choisissez-le dans les calques.',
      manyText:
        'Ces réglages se font un élément à la fois. Pour les aligner ensemble, passez à Position.',
      toArrange: 'Les aligner',
      chart: {
        name: 'Nom',
      },
      layersHint:
        'Le premier de la liste passe devant les autres : glissez une ligne pour changer l’ordre. Maj et un clic en sélectionnent plusieurs, et en glisser un les déplace tous.',
      drag: 'Glisser pour changer l’ordre',
      kinds: {
        text: 'Texte',
        image: 'Image',
        chart: 'Graphique',
        rectangle: 'Rectangle',
        ellipse: 'Ellipse',
        line: 'Ligne',
      },
      untitledText: 'Texte vide',
      forward: 'Avancer',
      backward: 'Reculer',
      duplicate: 'Dupliquer',
      remove: 'Retirer',
      position: 'Position et taille',
      x: 'X',
      y: 'Y',
      width: 'L',
      height: 'H',
      alignOnPage: 'Aligner sur la page',
      alignments: {
        left: 'Bord gauche',
        centre: 'Centré en largeur',
        right: 'Bord droit',
        top: 'Bord haut',
        middle: 'Centré en hauteur',
        bottom: 'Bord bas',
      },
      opacity: 'Opacité',
      rotation: 'Rotation',
      upright: 'Redresser',
      together: '{count} éléments sélectionnés',
      togetherHint:
        'Ils se déplacent ensemble sur la page. Maj et un clic en ajoutent ou en retirent un.',
      alignTogether: 'Aligner entre eux',
      spread: 'Répartir',
      spreads: {
        x: 'En largeur',
        y: 'En hauteur',
      },
      removeAll: 'Retirer les {count}',
      noColour: 'Aucune couleur',
      customColour: 'Une autre couleur',
      colours: {
        accent: 'Accent',
        ink: 'Encre',
        white: 'Blanc',
        grey: 'Gris',
        navy: 'Marine',
        teal: 'Sarcelle',
        sand: 'Sable',
      },
      text: {
        size: 'Corps',
        padding: 'Marge intérieure',
        align: 'Alignement',
        aligns: {
          left: 'À gauche',
          center: 'Centré',
          right: 'À droite',
          justify: 'Justifié',
        },
        colour: 'Texte',
        fill: 'Fond',
      },
      image: {
        picker: 'Image',
        fit: 'Cadrage',
        fits: {
          cover: 'Remplir',
          contain: 'Entière',
        },
        radius: 'Arrondi',
      },
      shape: {
        form: 'Forme',
        fill: 'Remplissage',
        stroke: 'Contour',
        strokeWidth: 'Épaisseur',
        radius: 'Arrondi',
      },
    },

    pageBreak: {
      note: 'Une nouvelle page commence ici. Le fond ci-dessous habille cette page-là.',
      spread: 'Double page',
      spreadHint:
        'La page qui commence ici s’étend sur deux pages côte à côte. Elle s’exporte en une seule page deux fois plus large.',
      background: 'Fond de page',
      backgroundHint: 'Couvre toute la page, marges comprises.',
      fit: 'Cadrage',
      fitHint: 'Comment l’image occupe la page.',
      fits: {
        cover: 'Remplir',
        contain: 'Entière',
        stretch: 'Étirer',
        tile: 'Répéter',
      },
      strength: 'Intensité',
      strengthHint: 'Une page qui porte du texte demande un fond discret.',
      strengths: {
        full: 'Pleine',
        soft: 'Douce',
        faint: 'Très douce',
      },
      bleedNote: 'Le débordement se règle dans la mise en forme, pour tout le document.',
    },
    fixedPage: {
      handleHint: 'Cliquer pour choisir, double-cliquer pour réécrire',
      empty: 'Cette page n’a ni texte ni arrière-plan.',
      source: 'Page {page} de {file}',
      frame: 'Texte choisi',
      asPrinted: 'Comme imprimé',
      rewritten: 'Réécrit',
      pickHint:
        'Cliquez un bloc de texte sur la page pour le choisir ; double-cliquez pour le réécrire sur place.',
      rewrite: 'Réécrire sur la page',
      restore: 'Revenir à l’imprimé',
      restoreAll: 'Remettre tous les blocs réécrits comme imprimés ({count})',
      missing:
        'Absents des polices du document, donc dessinés dans une police de remplacement : {characters}.',
      fixFonts: 'Régler les polices',
      outline: 'Montrer les blocs de texte',
      fonts: 'Polices de cette page',
      manageFonts: 'Gérer les polices',
      overflow:
        'Le texte dépasse son bloc d’{lines} ligne. Raccourcissez-le, ou revenez à l’imprimé. | Le texte dépasse son bloc de {lines} lignes. Raccourcissez-le, ou revenez à l’imprimé.',
      oversetHint: 'Le texte dépasse de ce bloc',
    },
    imagePage: {
      note: 'Une image seule, en plein cadre sur sa propre page. Rien d’autre ne s’y affiche : ni titre, ni texte, ni page voisine.',
      image: 'Image',
      imageHint: 'Remplit toute la page, bord à bord.',
      spread: 'Double page (planche)',
      spreadHint:
        'Pour une image conçue sur deux pages en vis-à-vis. À l’écran elle s’affiche en pleine largeur ; à l’export, sa moitié gauche et sa moitié droite s’impriment sur deux pages qui se suivent.',
    },
    tag: {
      label: 'Information ESRS',
      hint: 'La r\u00e9f\u00e9rence \u00e0 laquelle cette section r\u00e9pond : E1-6, S1-14, GOV-1. L\u2019index et le contr\u00f4le de couverture s\u2019en d\u00e9duisent.',
      notARef: 'Ce n\u2019est pas une r\u00e9f\u00e9rence ESRS.',
      materiality: 'Mat\u00e9rialit\u00e9',
      clear: 'Retirer',
    },

    directions: {
      label: 'Direction',
      clear: 'Retirer',
      open: 'Ouvert à toute personne qui peut modifier le rapport. Donnez-le à une direction pour le lui réserver.',
      inherited: 'Suit son chapitre : {names}.',
      ownerOnly: 'Seul le propriétaire du rapport et un administrateur peuvent le changer.',
    },

    settings: 'Réglages de la section',
    nothingSelected: 'Aucune sélection',
    idle: 'Choisissez une section dans la structure ou sur la page pour la modifier.',
    locked: 'Ce rapport est en cours de revue, son contenu ne peut donc pas être modifié.',
    textOnPage: 'Écrivez directement sur la page. Sélectionnez des mots pour les mettre en forme.',
    pageBreakNothing:
      'Un saut de page n’a rien à régler. Déplacez-le dans la structure pour changer où il tombe.',
    deselect: 'Désélectionner',
    duplicateSection: 'Dupliquer cette section',
    deleteSection: 'Supprimer cette section',

    chart: {
      title: 'Titre',
      type: 'Type',
      data: 'Données',
      series: 'Série',
      label: 'Catégorie',
      labelPlaceholder: 'Catégorie {number}',
      seriesPlaceholder: 'Série',
      removeLabel: 'Retirer cette catégorie',
      removeSeries: 'Retirer cette série',
      height: 'Hauteur',
      heightHint: '{mm} mm sur la page',
      legend: 'Afficher la légende',
      caption: 'Légende',
      types: {
        bar: 'Barres',
        line: 'Courbe',
        pie: 'Secteurs',
        doughnut: 'Anneau',
      },
      orientation: 'Barres',
      orientations: {
        vertical: 'Debout',
        horizontal: 'Couchées',
      },
      seriesColour: 'Couleur de cette série',
      labelColour: 'Couleur de cette catégorie',
      colourEach: 'Une couleur par barre',
      accentColours: 'Revenir aux couleurs de l’accent',
      stacked: 'Empiler les séries',
      values: 'Imprimer les chiffres sur le graphique',
      axis: 'Afficher l’échelle des valeurs',
      unit: 'Unité',
      unitHint: 'Imprimée après chaque chiffre : %, GWh, M€.',
      centre: 'Au centre',
      centreHint: 'Le texte posé dans l’anneau : le total, le plus souvent.',
      legendPosition: 'Légende',
      tabs: {
        label: 'Réglages du graphique',
        data: 'Données',
        look: 'Apparence',
      },
      legendPositions: {
        auto: 'Auto',
        top: 'Haut',
        right: 'Droite',
        bottom: 'Bas',
        left: 'Gauche',
      },
    },

    note: {
      number: 'Numéro',
      numberHint: 'Saisi, jamais recalculé : une note citée doit garder son numéro.',
      title: 'Intitulé',
      bodyOnPage: 'Écrivez le corps de la note directement sur la page.',
    },

    scope: {
      title: 'Titre',
      entityPlaceholder: 'Raison sociale',
      countryPlaceholder: 'Pays',
      methodPlaceholder: 'Méthode',
      removeEntry: 'Retirer cette entité',
      addEntry: 'Ajouter une entité',
      caption: 'Légende',
    },

    glossary: {
      title: 'Titre',
      termPlaceholder: 'Terme',
      definitionPlaceholder: 'Définition',
      removeEntry: 'Retirer ce terme',
      addEntry: 'Ajouter un terme',
    },

    divider: {
      variant: 'Type',
      space: 'Hauteur',
      spaceHint: '{mm} mm de blanc',
      variants: {
        rule: 'Filet',
        space: 'Blanc',
      },
    },

    datapoint: {
      label: 'Intitulé',
      labelPlaceholder: 'Émissions brutes de GES, scope 1',
      value: 'Valeur',
      unit: 'Unité',
      unitHint:
        'À côté du chiffre, jamais dedans : une unité saisie dans la valeur rend celle-ci illisible pour tout ce qui la relit.',
      unitPlaceholder: 'tCO2e',
      period: 'Période',
      periodPlaceholder: '2026',
      comparative: 'Comparatif',
      comparativePeriod: 'Période comparée',
      comparativePeriodPlaceholder: '2025',
      method: 'Méthode',
      methodHint:
        'Périmètre, méthode de calcul, estimations, exclusions. C’est ce que le vérificateur lit.',
    },

    target: {
      label: 'Intitulé',
      labelPlaceholder: 'Réduction des émissions scope 1 et 2',
      unit: 'Unité',
      unitPlaceholder: 'tCO2e',
      base: 'Référence',
      baseValue: 'Valeur de référence',
      baseYear: 'Année de référence',
      goal: 'Cible',
      targetValue: 'Valeur cible',
      targetYear: 'Année cible',
      current: 'Aujourd’hui',
      currentValue: 'Valeur actuelle',
      currentHint:
        'L’avancement se calcule à partir des trois chiffres : il n’est jamais saisi, donc il ne peut pas les contredire.',
      regressing: 'En recul',
      note: 'Base',
      noteHint: 'Absolue ou en intensité, scopes couverts, cible validée ou non par la science.',
    },

    materiality: {
      impact: 'Impact',
      financial: 'Financière',
      impactAxis: 'Matérialité d’impact →',
      financialAxis: '↑ Matérialité financière',
      topicPlaceholder: 'Sujet',
      addTopic: 'Ajouter un sujet',
      removeTopic: 'Retirer ce sujet',
      threshold: 'Seuil',
      thresholdHint: 'La ligne à partir de laquelle un sujet est retenu, sur les deux axes.',
      caption: 'Légende',
    },

    iro: {
      topic: 'Sujet',
      topicPlaceholder: 'Sujet',
      type: 'Nature',
      position: 'Chaîne de valeur',
      horizon: 'Horizon',
      conclusion: 'Conclusion',
      conclusionPlaceholder: 'Ce qui a été conclu',
      types: {
        impact: 'Impact',
        risk: 'Risque',
        opportunity: 'Opportunité',
      },
      positions: {
        upstream: 'Amont',
        own: 'Propres opérations',
        downstream: 'Aval',
      },
      horizons: {
        short: 'Court terme',
        medium: 'Moyen terme',
        long: 'Long terme',
      },
      addEntry: 'Ajouter une ligne',
      removeEntry: 'Retirer cette ligne',
      empty: 'Aucun impact, risque ou opportunité saisi pour le moment.',
      caption: 'Légende',
    },

    risks: {
      title: 'Risque',
      titlePlaceholder: 'Nom du risque',
      category: 'Famille de risques',
      categoryPlaceholder: 'Risques opérationnels',
      level: 'Criticité',
      levels: {
        high: 'Élevée',
        medium: 'Modérée',
        low: 'Faible',
      },
      description: 'Description',
      descriptionPlaceholder: 'Ce qui pourrait se produire, et ses effets',
      management: 'Gestion du risque',
      managementPlaceholder: 'Les dispositifs de maîtrise en place',
      addEntry: 'Ajouter un risque',
      removeEntry: 'Retirer ce risque',
      empty: 'Aucun risque saisi pour le moment.',
      uncategorised: 'Autres risques',
      caption: 'Légende',
    },

    ghg: {
      tabs: {
        label: 'Réglages de l’inventaire',
        lines: 'Émissions',
        settings: 'Réglages',
      },
      unit: 'Unit\u00e9',
      period: 'Exercice',
      comparativePeriod: 'Exercice compar\u00e9',
      line: 'Poste',
      linePlaceholder: 'Libell\u00e9 du poste',
      category: 'Cat\u00e9gorie',
      addLine: 'Ajouter un poste',
      removeLine: 'Retirer ce poste',
      intensityLabel: 'Intensit\u00e9',
      intensityHint:
        'Un total qui baisse pendant que l\u2019activit\u00e9 double n\u2019est pas une r\u00e9duction. L\u2019intensit\u00e9 se calcule \u00e0 partir du total et du d\u00e9nominateur.',
      intensityPlaceholder: 'tCO2e par M\u20ac de chiffre d\u2019affaires',
      intensityValue: 'D\u00e9nominateur',
      intensityComparative: 'D\u00e9nominateur compar\u00e9',
      caption: 'L\u00e9gende',
      scopes: {
        scope1: 'Scope 1 \u2013 \u00e9missions directes',
        scope2Location: 'Scope 2 \u2013 fond\u00e9 sur la localisation',
        scope2Market: 'Scope 2 \u2013 fond\u00e9 sur le march\u00e9',
        scope3: 'Scope 3 \u2013 autres \u00e9missions indirectes',
      },
      subtotals: {
        scope1: 'Total scope 1',
        scope2Location: 'Total scope 2 (localisation)',
        scope2Market: 'Total scope 2 (march\u00e9)',
        scope3: 'Total scope 3',
      },
      totalMarket: 'Total (scope 2 fond\u00e9 sur le march\u00e9)',
      totalLocation: 'Total (scope 2 fond\u00e9 sur la localisation)',
      categories: {
        '1': 'Achats de biens et services',
        '2': 'Biens d\u2019\u00e9quipement',
        '3': '\u00c9missions li\u00e9es \u00e0 l\u2019\u00e9nergie non incluses dans les scopes 1 et 2',
        '4': 'Transport et distribution amont',
        '5': 'D\u00e9chets g\u00e9n\u00e9r\u00e9s',
        '6': 'D\u00e9placements professionnels',
        '7': 'D\u00e9placements domicile-travail',
        '8': 'Actifs lou\u00e9s amont',
        '9': 'Transport et distribution aval',
        '10': 'Transformation des produits vendus',
        '11': 'Utilisation des produits vendus',
        '12': 'Fin de vie des produits vendus',
        '13': 'Actifs lou\u00e9s aval',
        '14': 'Franchises',
        '15': 'Investissements',
      },
      problems: {
        unreadable:
          '{count} chiffre n\u2019a pas pu \u00eatre lu et n\u2019entre dans aucun total. | {count} chiffres n\u2019ont pas pu \u00eatre lus et n\u2019entrent dans aucun total.',
        noScope2Basis:
          'Le scope 2 n\u2019est renseign\u00e9 que sur une base. Les deux sont attendues, et publier une seule sans le dire est l\u2019omission la plus courante.',
        categoryTwice:
          'La cat\u00e9gorie « {category} » appara\u00eet deux fois : elle est compt\u00e9e deux fois dans le total.',
      },
    },

    taxonomy: {
      kpi: 'Indicateur',
      kpis: {
        turnover: 'Chiffre d\u2019affaires',
        capex: 'CapEx',
        opex: 'OpEx',
      },
      total: 'Total',
      eligible: '\u00c9ligible',
      eligibleShare: 'Part \u00e9ligible',
      aligned: 'Align\u00e9',
      alignedShare: 'Part align\u00e9e',
      unit: 'Unit\u00e9',
      unitPlaceholder: 'En milliers d\u2019euros',
      period: 'Exercice',
      caption: 'L\u00e9gende',
      impossible:
        'Le montant align\u00e9 d\u00e9passe le montant \u00e9ligible. Une activit\u00e9 doit \u00eatre \u00e9ligible avant d\u2019\u00eatre align\u00e9e : l\u2019un des deux chiffres est faux.',
    },

    index: {
      lead: 'L\u2019index se construit \u00e0 partir des r\u00e9f\u00e9rences ESRS port\u00e9es par les sections, comme le sommaire se construit \u00e0 partir des titres.',
      showTitles: 'Afficher les intitul\u00e9s',
      showTitlesHint:
        'Le titre de la section vis\u00e9e, \u00e0 c\u00f4t\u00e9 de sa r\u00e9f\u00e9rence.',
      empty: 'Aucune section ne porte encore de r\u00e9f\u00e9rence ESRS.',
      check: 'Contr\u00f4le de couverture',
      noGaps: 'Rien \u00e0 signaler.',
      gaps: {
        declaredNotDisclosed:
          '{standard} est d\u00e9clar\u00e9 mat\u00e9riel, mais aucune exigence de cette norme n\u2019est renseign\u00e9e.',
        duplicated: 'La r\u00e9f\u00e9rence {ref} est revendiqu\u00e9e par deux sections.',
      },
    },

    cover: {
      arrangement: 'Disposition',
      variants: {
        classic: 'Classique',
        centred: 'Centr\u00e9e',
        band: 'Bandeau',
        portrait: 'Photo',
        framed: 'Encadr\u00e9e',
        sidebar: 'Bande lat\u00e9rale',
        full: 'Pleine page',
      },
      logo: 'Logo',
      logoHint:
        'Un logotype, de pr\u00e9f\u00e9rence sur fond transparent. Sur un bandeau color\u00e9, il est imprim\u00e9 en r\u00e9serve.',
      picture: 'Image',
      pictureHint:
        'Une photographie ou une illustration. Elle est recadr\u00e9e pour remplir la zone que la disposition lui r\u00e9serve.',
      note: 'Mention de pied',
      noteHint: 'Une r\u00e9f\u00e9rence, une version, une mention de confidentialit\u00e9.',
      notePlaceholder: 'Document confidentiel \u2013 diffusion restreinte',
      entity: 'Entité',
      entityPlaceholder: 'Raison sociale',
      subtitle: 'Sous-titre',
      titleStyle: 'Style de texte, titre',
      subtitlePlaceholder: 'Comptes annuels',
      period: 'Période',
      periodPlaceholder: 'Exercice clos le 31 décembre 2026',
      standard: 'Référentiel',
      standardPlaceholder: 'IFRS',
      standardHint: 'Affiché au-dessus du titre, en petites capitales.',
    },

    group: {
      name: 'Nom',
      showTitle: 'Imprimer le nom comme titre',
      note: 'Désactivez ceci pour un groupe qui ne sert qu’à organiser la structure. Il garde ses sections, il n’apparaît simplement pas dans le document.',
      columns: 'Colonnes',
      columnsHint:
        'Le nombre de sections côte à côte. L’ordre du document ne change pas : de gauche à droite, puis à la ligne.',
      perLine: '{count} par ligne',
      align: 'Alignement',
      alignHint: 'Comment se calent des sections de hauteurs différentes sur une même ligne.',
      alignments: {
        stretch: 'Égalisées',
        start: 'En haut',
        center: 'Centrées',
      },
      count: '{count} à l’intérieur',
    },

    heading: {
      text: 'Texte',
      level: 'Niveau',
      levelHint: 'Les niveaux 1 à 3 apparaissent au sommaire.',
      levelGroup: 'Niveau de titre',
      inContents: 'Dans le sommaire',
      listed: 'Listé dans le sommaire',
      contentsLabel: 'Ligne du sommaire',
      contentsLabelHint: 'Ce que le sommaire dit à la place du titre. Vide : le titre.',
      contentsDetail: 'Ligne sous l’entrée',
      contentsDetailHint:
        'Quelques mots sous l’entrée. Les pages qu’elle couvre s’ajoutent toutes seules.',
      chapterColour: 'Couleur du chapitre',
      chapterColourAuto: 'Couleur suivante de la palette',
      houseColour: 'Couleur de la charte {number}',
      textStyleHint:
        'Composé dans l’un des styles de texte du document plutôt que dans le style des titres.',
    },

    text: {
      tabs: {
        label: 'Réglages du texte',
        format: 'Mise en forme',
        layout: 'Mise en page',
      },
      columns: 'Colonnes',
      columnsHint:
        'Deux ou trois colonnes font s’écouler le même texte de l’une à la suivante, comme dans un journal.',
      columnCounts: {
        1: '1 colonne',
        2: '2 colonnes',
        3: '3 colonnes',
      },
      textStyle: 'Style de texte',
      textStyleHint:
        'S’applique à tout le bloc. Un mot mis en couleur depuis la barre d’outils, à l’intérieur, garde sa propre couleur.',
      textStyleNone: 'Aucun style',
      layout: {
        gap: 'Espace entre colonnes',
        gaps: {
          narrow: 'Serré',
          normal: 'Normal',
          wide: 'Large',
        },
        rule: 'Un filet entre les colonnes',
        align: 'Alignement',
        aligns: {
          document: 'Document',
          left: 'Gauche',
          justify: 'Justifié',
          center: 'Centré',
        },
        leading: 'Interligne',
        leadings: {
          document: 'Document',
          tight: 'Serré',
          normal: 'Normal',
          loose: 'Aéré',
        },
        frame: 'Encadré',
        frameHint:
          'Détache le bloc : une teinte derrière, un filet autour, ou la teinte de l’accent.',
        frames: {
          none: 'Aucun',
          tint: 'Teinté',
          outline: 'Filet',
          accent: 'Accent',
        },
        hyphens: 'Couper les mots en fin de ligne',
        dropCap: 'Lettrine sur la première lettre',
      },
      format: {
        idle: 'Cliquez dans le texte sur la page, puis choisissez ici : la mise en forme s’applique à la sélection.',
        style: 'Style',
        characters: 'Caractères',
        size: 'Taille',
        sizes: {
          small: 'Petite',
          normal: 'Normale',
          large: 'Grande',
          xlarge: 'Plus grande',
        },
        ink: 'Couleur du texte',
        inks: {
          auto: 'Automatique',
          accent: 'Accent',
          ink: 'Encre',
          grey: 'Gris',
          red: 'Rouge',
          orange: 'Orange',
          green: 'Vert',
          teal: 'Sarcelle',
          blue: 'Bleu',
          violet: 'Violet',
          house: 'Couleur {n} de la charte',
        },
        houseInks: 'Charte',
        highlight: 'Surlignage',
        highlights: {
          none: 'Aucun',
          yellow: 'Jaune',
          green: 'Vert',
          blue: 'Bleu',
          pink: 'Rose',
          grey: 'Gris',
        },
        paragraph: 'Paragraphe',
        align: 'Alignement',
        aligns: {
          left: 'À gauche',
          center: 'Centré',
          right: 'À droite',
          justify: 'Justifié',
        },
        lists: 'Listes et citation',
        paragraphTools: {
          bullets: 'Liste à puces',
          numbers: 'Liste numérotée',
          outdent: 'Diminuer le retrait',
          indent: 'Augmenter le retrait',
          quote: 'Citation',
        },
        link: 'Lien',
        linkPlaceholder: 'https://',
        linkApply: 'Appliquer',
        linkRemove: 'Retirer',
      },
    },

    table: {
      tabs: {
        label: 'Réglages du tableau',
        cells: 'Cellules',
        look: 'Apparence',
        settings: 'Réglages',
      },
      title: 'Titre',
      caption: 'Légende',
      captionHint: 'Imprimée sous le tableau, pour une source ou une base.',
      fit: 'Largeur',
      fitHint:
        'Comment le tableau tient dans la page. « À la page » réduit le tableau entier pour qu’il rentre, comme dans Excel.',
      fitOptions: {
        normal: 'Normal',
        shrink: 'À la page',
        compact: 'Compact',
      },
      columnWidths: 'Largeurs de colonnes',
      columnWidthsHint:
        'Pourcentages de la largeur du tableau. Laissées vides, les colonnes suivent leur contenu.',
      columnsNarrow:
        '{count} colonne en orange est trop étroite pour ses mots, qui se coupent lettre par lettre : élargissez-la, ou répartissez les largeurs également. | Les {count} colonnes en orange sont trop étroites pour leurs mots, qui se coupent lettre par lettre : élargissez-les, ou répartissez les largeurs également.',
      columnWidthsSet: 'Régler les largeurs',
      columnWidthsEqual: 'Répartir également',
      columnWidthsAuto: 'Auto (contenu)',
      style: {
        title: 'Style',
        none: 'Aucun style (le document seul)',
        byDefault: 'Par défaut : {name}',
        library: 'Bibliothèque de styles…',
        adjusted: '{count} réglage ajusté sur ce tableau | {count} réglages ajustés sur ce tableau',
        missing: 'Le style que suivait ce tableau n’est plus dans la bibliothèque.',
        revert: 'Revenir au style',
        revertAll: 'Revenir au style',
      },
      look: {
        title: 'Apparence',
        reset: 'Revenir à celle du document',
        document: 'Document',
        head: 'En-tête',
        heads: {
          underline: 'Souligné',
          tinted: 'Teinté',
          filled: 'Plein',
          plain: 'Sobre',
        },
        colour: 'Couleur',
        documentColour: 'La couleur d’accent du document',
        customColour: 'Une autre couleur',
        lines: 'Filets',
        rows: 'Hauteur des lignes',
        banded: 'Lignes alternées',
        labels: 'Première colonne en libellés, en gras',
        total: 'Dernière ligne en total',
        negatives: 'Chiffres négatifs',
        negativesHint:
          'Seul l’affichage change : le chiffre garde son signe dans le tableau et dans CSRD.',
        negativeStyles: {
          minus: '−1 467',
          parentheses: '(1 467)',
        },
        headCase: 'Mots de l’en-tête',
        headCases: { normal: 'Tels quels', upper: 'MAJUSCULES' },
        tint: 'Teinte du bandeau',
        derivedTint: 'Déduite de la couleur',
        totals: 'Lignes de total',
        totalsHint: 'Les lignes qui ont une cellule en gras, ce que CSRD pose pour ses totaux.',
        totalsStyles: { fill: 'Bandeau teinté', bold: 'Gras seul' },
      },
      cells: 'Cellules',
      row: 'Ligne',
      column: 'Colonne',
      columnPlaceholder: 'Colonne {number}',
      markNumeric: 'Traiter cette colonne comme des montants',
      selectTable: 'Sélectionner tout le tableau',
      selectHeader: 'Sélectionner la ligne d’en-tête',
      selectRow: 'Sélectionner la ligne {number}',
      selectColumn: 'Sélectionner la colonne {number}',
      headerHandle: 'T',
      selectHint:
        'Cliquez sur une cellule, un numéro de ligne ou de colonne pour la mettre en forme. Glissez sur plusieurs cellules, ou Maj+clic, pour en choisir un bloc à fusionner.',
      selection: {
        cell: 'Cellule, ligne {row}, colonne {column}',
        headerCell: 'En-tête, colonne {column}',
        row: 'Ligne {row}',
        header: 'Ligne d’en-tête',
        column: 'Colonne {column}',
        range: 'Bloc de {rows} × {columns}',
        table: 'Tout le tableau',
      },
      deselect: 'Désélectionner',
      align: 'Alignement',
      aligns: {
        left: 'À gauche',
        center: 'Centré',
        right: 'À droite',
      },
      strong: 'En évidence',
      strongHint: 'Pour un total ou un sous-total : en gras, avec un filet au-dessus.',
      ruleAbove: 'Filet au-dessus',
      ruleAboveHint: 'Un filet sur le bord haut des cellules choisies, dans la couleur du tableau.',
      ruleBelow: 'Filet en dessous',
      ruleBelowHint: 'Un filet sur le bord bas des cellules choisies, dans la couleur du tableau.',
      numeric: 'Montants',
      fill: 'Fond',
      ink: 'Texte',
      noColour: 'Aucune couleur',
      customColour: 'Une autre couleur',
      fills: {
        grey: 'Gris',
        blue: 'Bleu',
        green: 'Vert',
        amber: 'Ambre',
        rose: 'Rose',
        teal: 'Sarcelle',
      },
      inks: {
        ink: 'Encre',
        grey: 'Gris',
        navy: 'Marine',
        green: 'Vert',
        red: 'Rouge',
      },
      rows: 'Lignes',
      columns: 'Colonnes',
      rowAbove: 'Ligne au-dessus',
      rowBelow: 'Ligne en dessous',
      columnLeft: 'Colonne à gauche',
      columnRight: 'Colonne à droite',
      moveUp: 'Monter',
      moveDown: 'Descendre',
      moveLeft: 'Vers la gauche',
      moveRight: 'Vers la droite',
      deleteRow: 'Supprimer la ligne',
      deleteColumn: 'Supprimer la colonne',
      clear: 'Vider',
      clearHint: 'Efface le contenu et la mise en forme de la sélection.',
      openEditor: 'Ouvrir l’éditeur de tableau',
      openEditorHint:
        'En grand, avec un aperçu du tableau tel qu’il sera imprimé. Un double-clic sur le tableau dans la page l’ouvre aussi.',
      editorTitle: 'Tableau : {title}',
      editorTitleBare: 'Tableau',
      preview: 'Aperçu, tel qu’imprimé',
      previewHint: 'Cliquez sur une cellule pour la sélectionner, Maj+clic pour un bloc.',
      merging: 'Fusion',
      merge: 'Fusionner les cellules',
      mergeHint: 'Réunit le bloc en une seule cellule, qui garde le premier texte.',
      mergeLoses:
        'Le premier texte est gardé, {count} autre est retiré. | Le premier texte est gardé, les {count} autres sont retirés.',
      split: 'Séparer',
      splitHint: 'Rend à chaque cellule sa place. Le texte reste dans la première.',
      tiedRow: 'Cette ligne fait partie d’une cellule fusionnée : séparez-la pour la déplacer.',
      tiedColumn:
        'Cette colonne fait partie d’une cellule fusionnée : séparez-la pour la déplacer.',
    },

    kpi: {
      title: 'Titre',
      labelPlaceholder: 'Chiffre d’affaires',
      valuePlaceholder: '128,4 M€',
      changePlaceholder: '+4,2 %',
      removeFigure: 'Retirer ce chiffre',
      addFigure: 'Ajouter un chiffre',
      toneGroup: 'Comment lire la variation',
      neutral: 'Neutre',
      good: 'Favorable',
      poor: 'Défavorable',
    },

    image: {
      choose: 'Choisir une image, ou la d\u00e9poser ici',
      replace: 'Remplacer',
      remove: 'Retirer',
      file: 'Fichier',
      caption: 'Légende',
      width: 'Largeur',
      widthHint: '{percent} % de la largeur du texte',
      notAnImage: 'Ce n’est pas une image.',
      tooLarge: 'Cette image dépasse 4 Mo. Réduisez-la et réessayez.',
      unreadable: 'Ce fichier n’a pas pu être lu.',
    },

    contents: {
      title: 'Titre',
      depth: 'Profondeur',
      depthHint: 'Combien de niveaux de titre lister.',
      subtitle: 'Sous-titre',
      pageNumbers: 'Numéros de page',
      showDetail: 'Ligne sous chaque entrée',
      levelStyle: 'Style de texte, niveau {level}',
      levelStyleHint:
        'Les styles de texte du document, un par niveau : changez le style et tous les sommaires suivent.',
      levelStyleNone: 'Comme la page le compose',
      note: 'La liste est calculée à partir du document à chaque affichage, elle ne se périme donc jamais.',
    },

    signatures: {
      statement: 'Mention',
      statementHint: 'Imprimée au-dessus des signatures.',
      statementPlaceholder:
        'Les comptes annuels ont été arrêtés par le conseil et signés en son nom.',
      namePlaceholder: 'Nom',
      rolePlaceholder: 'Fonction',
      placePlaceholder: 'Lieu et date',
      removeSignatory: 'Retirer ce signataire',
      addSignatory: 'Ajouter un signataire',
    },
  },

  workflow: {
    states: {
      brouillon: 'Brouillon',
      en_revue: 'En revue',
      valide: 'Validé',
      diffuse: 'Diffusé',
      rejete: 'Rejeté',
    },
    actions: {
      brouillon: 'Rouvrir pour modification',
      en_revue: 'Soumettre à la revue',
      valide: 'Valider',
      diffuse: 'Diffuser',
      rejete: 'Renvoyer',
    },
  },

  fonts: {
    title: 'Polices',
    leadNeeding:
      '{count} police n’est pas complète : ce qui était imprimé s’affiche à l’identique, les nouveaux caractères utilisent un remplacement. | {count} polices ne sont pas complètes : ce qui était imprimé s’affiche à l’identique, les nouveaux caractères utilisent un remplacement.',
    leadComplete: 'Toutes les polices sont complètes : on peut y écrire n’importe quel texte.',
    none: 'Aucune page importée n’utilise encore de police propre.',
    sample: 'Résultats 2025 : chiffre d’affaires en hausse de 12 % ?!',
    meta: 'Graisse {weight}',
    italic: 'italique',
    characters: '{count} caractère issu du PDF | {count} caractères issus du PDF',
    status: {
      complete: 'Complète',
      partial: 'Issue du PDF',
      missing: 'Manquante',
    },
    explain: {
      complete:
        'La police entière est disponible : chaque caractère, imprimé ou saisi, y est dessiné.',
      partial:
        'Reprise du PDF, qui ne contenait que les caractères imprimés. Le reste est dessiné en {substitute} jusqu’à ce que le fichier de la police soit importé.',
      missing:
        'Le PDF ne contenait pas cette police. Tout y est dessiné en {substitute} : importez le fichier de la police pour l’afficher comme imprimée.',
    },
    missingInSample: 'Dessinés ici dans le remplacement : {characters}',
    upload: 'Importer le fichier de la police',
    replaceFile: 'Utiliser un autre fichier',
    removeFile: 'Retirer le fichier',
    replaceWith: 'Remplacement',
    reading: 'Lecture…',
    problems: {
      kind: 'Choisissez un fichier .woff2, .woff, .ttf ou .otf.',
      size: 'Ce fichier est trop volumineux pour être une seule police.',
      unreadable: 'Ce fichier n’a pas pu être lu comme une police.',
    },
  },
  importing: {
    selectAll: 'Tout prendre',
    selectNone: 'Ne rien prendre',

    // Counted things, shared by both imports. A count is almost never
    // alone in a sentence, so these are fragments the dialogs compose
    // rather than sentences: '3 lignes sur 1 colonne' needs each half to
    // agree on its own, and no single message can do that for two counts.
    rows: '{count} ligne | {count} lignes',
    columns: '{count} colonne | {count} colonnes',
    paragraphs: '{count} paragraphe | {count} paragraphes',

    pdf: {
      title: 'Importer un PDF',
      subtitle: 'En pages du rapport, ou exactement comme imprimées',
      steps: {
        file: 'Fichier',
        analysis: 'Analyse',
        pages: 'Pages',
        fonts: 'Polices',
      },
      lead: 'Choisissez le rapport de l’an dernier. Ses pages reviennent en sections du rapport (titres, texte, tableaux et images, dans ses polices et ses styles), ou exactement comme imprimées.',
      choose: 'Choisir un PDF',
      uploading: 'Envoi du fichier…',
      wrongType: 'Choisissez un fichier .pdf.',
      unreadable: 'Ce fichier n’a pas pu être lu comme un PDF.',
      stages: {
        reading: 'Lecture des pages',
        tables: 'Lecture des tableaux',
        interpreting: 'Reconnaissance de la structure',
        fonts: 'Récupération des polices',
        pictures: 'Dessin des arrière-plans',
      },
      stageHints: {
        reading: 'Chaque mot avec sa police, sa taille, sa couleur et sa position.',
        tables: 'Les tableaux sans filets, remis en lignes et en colonnes.',
        interpreting: 'Titres, paragraphes, listes, et ce qui se tient à part du texte.',
        fonts: 'Les polices que contient le PDF, rendues réutilisables.',
        pictures: 'Chaque page sans son texte, et ses images détachées.',
      },
      counted: '{done} sur {total}',
      live: {
        title: 'Ce que l’import a trouvé jusqu’ici',
        page: 'Page {page}',
        waiting: 'En attente',
        read: 'Lue',
        flowing: 'Document, {count} colonne | Document, {count} colonnes',
        designed: 'Mise en page libre',
        looked: 'Vue par l’IA',
        kinds: {
          heading: 'Titre',
          text: 'Texte',
          table: 'Tableau',
          chart: 'Graphique',
          picture: 'Image',
          free: 'Placé librement',
          running: 'En-tête ou pied de page',
        },
      },
      chosenCount: '{count} page sur {total} choisie | {count} pages sur {total} choisies',
      all: 'Toutes',
      none: 'Aucune',
      range: 'Pages à choisir',
      rangePlaceholder: '1-12, 15',
      apply: 'Choisir',
      pagesHints: {
        components:
          'Chaque page ouvre une page du rapport : son texte courant en titres, textes et tableaux, tout le reste placé librement sur son arrière-plan.',
        exact:
          'Chaque page devient une page du rapport, redessinée mot pour mot, sur sa propre feuille.',
      },
      modes: {
        label: 'Comment les pages arrivent',
        recommended: 'Conseillé',
        components: {
          name: 'En composants',
          hint: 'Des titres, textes, tableaux et images que vous modifiez, déplacez et faites recouler comme toute page du rapport, dans les polices et les styles du PDF.',
        },
        exact: {
          name: 'Copie exacte',
          hint: 'Chaque mot exactement là où il était imprimé. Le texte se réécrit sur place, mais rien ne recoule.',
        },
      },
      layouts: {
        flowing: 'texte · {count} colonne | texte · {count} colonnes',
        designed: 'mise en page libre',
      },
      pageLabel: 'Page {page}',
      scanned: 'scan',
      fontsLead:
        '{complete} polices sur {total} sont complètes. Décidez ici du sort des autres, ou plus tard depuis la page.',
      matchPaper: 'Aligner le format du document sur celui du PDF ({width} × {height})',
      matchLayout:
        'Aligner le format ({width} × {height}), les marges et la taille du texte du document sur ceux du PDF',
      fontsUrge: {
        title: '{count} police n’est pas complète | {count} polices ne sont pas complètes',
        body: 'Le texte importé en composants est fait pour être réécrit, et une police que le PDF ne contenait qu’en partie dessine chaque caractère manquant avec sa police de remplacement. Importez les vrais fichiers de police pour un texte juste à mesure qu’il est modifié.',
      },
      confirmFonts:
        '{count} police est encore incomplète : le texte réécrit mêlera sa police de remplacement. Importez-la d’abord, ou importez quand même. | {count} polices sont encore incomplètes : le texte réécrit mêlera leurs polices de remplacement. Importez-les d’abord, ou importez quand même.',
      importAnyway: 'Importer quand même',
      toFonts: 'Suite : polices',
      import: 'Importer {count} page | Importer {count} pages',
      importing: 'Import en cours…',
      pageTitle: 'Page {page} de {file}',
      layerTitle: 'Placé sur la page {page}',
      textTitle: 'Texte, page {page}',
      tableTitle: 'Tableau, page {page}',
      chartTitle: 'Graphique, page {page}',
      spaceTitle: 'Espace, page {page}',
      styleName: '{role} {size} pt',
      roles: {
        title: 'Titre',
        h1: 'Titre 1',
        h2: 'Titre 2',
        h3: 'Titre 3',
        body: 'Corps',
        bullet: 'Liste',
        caption: 'Légende',
        note: 'Note',
        key_figure: 'Chiffre clé',
        table_header: 'En-tête de tableau',
        table_body: 'Tableau',
        other: 'Texte',
      },
    },
    word: {
      title: 'Importer depuis Word',
      lead: 'Les titres, les paragraphes et les tableaux arrivent comme des sections que vous pouvez réordonner et commenter. Les images, non.',
      choose: 'Choisir un fichier .docx, ou le déposer ici',
      reading: 'Lecture...',
      unreadable: 'Ce fichier n’a pas pu être lu. Il doit s’agir d’un .docx.',
      wrongType: 'Il faut un fichier .docx.',
      nothing: 'Rien n’a pu être lu dans ce document.',
      chosen: '{count} sur {total} retenue | {count} sur {total} retenues',
      addChosen: 'Ajouter {count} section | Ajouter {count} sections',
      keepSection: 'Retenir cette section',
      keepChapter: 'Retenir ce chapitre entier',
      keepParagraph: 'Retenir ce paragraphe',
      showParagraphs: 'Voir le détail des paragraphes',
      hideParagraphs: 'Masquer le détail',
      keepLooks: 'Reprendre la mise en forme des tableaux',
      keepLooksHint: 'Fond et couleur des cellules, gras et alignement, tels que dans le document.',
      pickToPreview: 'Choisissez un élément à gauche pour le lire en entier.',
      droppedParagraph: 'Ce paragraphe ne sera pas importé.',
      imageLeftOut: 'Une image a été laissée de côté. Ajoutez-la avec la section Image.',
      imagesLeftOut: '{count} images ont été laissées de côté. Ajoutez-les avec la section Image.',
      simplified:
        '{count} élément de mise en forme a été simplifié. | {count} éléments de mise en forme ont été simplifiés.',
    },

    excel: {
      title: 'Importer depuis Excel',
      lead: 'Choisissez la feuille, puis la plage de cellules à reprendre. Les colonnes de montants sont alignées à droite et les totaux mis en évidence.',
      choose: 'Choisir un classeur, ou le déposer ici',
      reading: 'Lecture...',
      unreadable: 'Ce fichier n’a pas pu être lu. Il doit s’agir d’un .xlsx ou .xls.',
      wrongType: 'Il faut un fichier .xlsx ou .xls.',
      noTable:
        'Aucune feuille de ce classeur ne contient de tableau. Vous pouvez tout de même choisir une plage à la main.',
      sheets: 'Feuilles du classeur',
      range: 'Plage',
      badRange: 'Plage introuvable',
      rangeSize: '{rows} sur {columns}',
      suggested: 'Tableau repéré',
      suggestedHint: 'Revenir à l’endroit où le tableau semble se trouver.',
      wholeSheet: 'Toute la feuille',
      dragHint:
        'Glissez sur la feuille pour choisir la plage. Cliquez une lettre pour une colonne entière, un numéro pour une ligne, ou saisissez la référence, par exemple C4:H38.',
      beyond:
        '{rows} et {columns} de plus ne sont pas affichées. Saisissez leur référence pour les reprendre.',
      headerRow: 'Première ligne',
      header: {
        auto: 'Au jugé',
        firstRow: 'Ce sont les en-têtes',
        none: 'Ce sont des données',
      },
      keepLooks: 'Reprendre la mise en forme du classeur',
      keepLooksHint: 'Fond et couleur des cellules, gras et alignement.',
      fit: 'Largeur',
      fitHint:
        'Pour un tableau à nombreuses colonnes, « Ajuster à la page » le réduit entièrement pour qu’il tienne dans la largeur.',
      fitOptions: {
        normal: 'Normal',
        shrink: 'Ajuster à la page',
        compact: 'Compact',
      },
      takeThisSheet: 'Reprendre cette feuille',
      asImported: 'Tel qu’il sera importé',
      tableSize: '{rows} · {columns}',
      moreRows: 'et {count} ligne de plus | et {count} lignes de plus',
      sheetEmpty: 'Cette feuille est vide.',
      pickRange: 'Choisissez une plage sur la feuille.',
      rangeTooShort:
        'Il faut au moins deux lignes. Avec une seule, rien ne dit si ce sont des en-têtes ou des chiffres.',
      rangeEmpty: 'Il n’y a rien dans cette plage.',
      add: 'Ajouter {count} tableau | Ajouter {count} tableaux',
    },

    csrd: {
      title: 'Importer depuis CSRD',
      lead: 'Les textes et les tableaux collectés dans CSRD arrivent comme des sections, chaque exigence repérée par sa référence.',
      connecting: 'Connexion à CSRD...',
      signIn:
        'Connectez-vous via le portail pour accéder à CSRD. Sans cela, CSRD ne peut pas savoir qui le demande.',
      devToken:
        'CSRD a refusé le jeton de développement, qui dure environ une heure. Déposez-en un nouveau dans le fichier lu par l’API (CSRD_DEV_TOKEN_FILE).',
      noClient: 'Votre compte portail n’est rattaché à aucun client CSRD.',
      forbidden: 'Votre profil CSRD ne permet pas de consulter la collecte.',
      unreachable: 'CSRD est injoignable. Réessayez dans un instant.',
      failed:
        'CSRD a répondu par une erreur en lisant cette collecte. Si cela se reproduit, le problème vient de CSRD.',
      noReporting: 'Aucun reporting n’existe encore dans CSRD.',
      noEntities: 'Aucune entité CSRD ne vous est ouverte.',
      signedInAs: 'Connecté en tant que',
      reporting: 'Reporting',
      reportingHint: 'Celui sélectionné dans CSRD. Pour en lire un autre, changez-le dans CSRD.',
      reportingOf: 'Reporting à lire',
      principal: 'principal',
      secondary: 'secondaire',
      secondaryHint:
        'CSRD sera placé sur ce reporting le temps de la lecture, puis remis sur {principal}.',
      otherReporting:
        'CSRD est sur le reporting {current}, et non sur le principal de ce rapport ({principal}).',
      moveTo: 'Passer sur {code} dans CSRD',
      moving: 'Changement dans CSRD...',
      entity: 'Entité',
      loadingList: 'Lecture de la collecte...',
      nothingListed: 'Rien n’est collecté pour cette entité.',
      chosen: '{count} sur {total} choisies',
      progress: 'Renseignée à {count} %',
      takeStandard: 'Tout prendre dans {standard}',
      takeDisclosure: 'Prendre {name}',
      read: 'Lire {count} exigence | Lire {count} exigences',
      reading: 'Lecture...',
      nothingCollected: 'Rien n’a été collecté dans les exigences choisies.',
      emptyLeftOut:
        '{count} exigence sans donnée collectée a été écartée. | {count} exigences sans donnée collectée ont été écartées.',
      back: 'Retour',
      caption: 'Source : CSRD, {reporting}, {entity}, lu le {date}',
    },
  },

  collaboration: {
    comments: 'Commentaires',
    newComment: 'Nouveau commentaire',
    discussion: 'Discussion',
    noComments:
      'Aucun commentaire. Sélectionnez une section sur la page et utilisez sa marque de commentaire pour en ouvrir un.',
    willStart: 'Ceci ouvrira une discussion sur la section sélectionnée.',
    write: 'Écrire un commentaire',
    backToAll: 'Revenir à tous les commentaires',
    closeComments: 'Fermer les commentaires',
    resolve: 'Résoudre',
    reopen: 'Rouvrir',
    resolved: 'Résolu',
    messageCount: '{count} messages',
    showOnPage: 'Voir sur la page',
    withdrawn: 'Retiré',

    share: {
      title: 'Partager ce rapport',
      who: 'Qui inviter',
      placeholder: 'Choisir une personne',
      nobody: 'Plus personne à inviter. Ajoutez des personnes dans l’écran Accès.',
      invite: 'Inviter',
      whatTheyCanDo: 'Ce qu’ils peuvent faire',
      owner: 'Propriétaire',
      invited: 'Invité',
      removeOne: 'Retirer {name}',
      roles: {
        commenter: 'Commenter',
        commenterHint: 'Le lire et en discuter',
        reviewer: 'Modifier',
        reviewerHint: 'Changer son contenu',
        validateur: 'Valider',
        validateurHint: 'Le signer, ou le renvoyer',
      },
    },

    remote: {
      saved: '{name} a enregistré une version plus récente de ce rapport.',
      take: 'Prendre leur version',
      keep: 'Garder la mienne',
    },
  },

  exporting: {
    title: 'Exporter',
    pdf: 'PDF',
    pdfHint: 'Exactement tel qu’il apparaît ici. Pour l’envoi et la signature.',
    html: 'HTML',
    htmlHint:
      'La même page, comme fichier. S’ouvre dans n’importe quel navigateur, sans rien installer.',
    xlsx: 'Excel',
    xlsxHint: 'Les tableaux comme des montants exploitables.',
    docx: 'Word',
    docxHint: 'Pour un rapport qui doit encore être retravaillé à la main.',
    working: 'Production en cours...',
    failed: 'Le fichier n’a pas pu être produit.',
    note: 'Le premier PDF après un redémarrage prend quelques secondes de plus : le serveur lance un navigateur pour l’imprimer, puis le garde pour les suivants.',
  },

  theme: {
    toLight: 'Passer en clair',
    toDark: 'Passer en sombre',
  },

  language: {
    label: 'Langue',
    fr: 'Français',
    en: 'English',
  },

  kit: {
    title: 'Kit d’interface',
    subtitle:
      'Les composants de Quantum, et ceux ajoutés pour le constructeur dans le même style. Chacun suit le thème clair et le thème sombre.',
    theme: 'Thème',
    light: 'Clair',
    dark: 'Sombre',
    sections: {
      actions: 'Actions',
      status: 'Statuts',
      forms: 'Formulaires',
      feedback: 'Retours',
      figures: 'Chiffres',
      navigation: 'Navigation',
      overlays: 'Dialogues et personnes',
    },
    buttons: {
      primary: 'Exporter',
      secondary: 'Partager',
      soft: 'Ajouter une section',
      ghost: 'Commentaires',
      link: 'Voir l’historique',
      danger: 'Supprimer',
      small: 'Petit',
      large: 'Grand',
      pill: 'Filtrer',
      loading: 'Enregistrement',
      disabled: 'Indisponible',
    },
    tones: {
      neutral: 'Brouillon',
      blue: 'En cours',
      cyan: 'Importé',
      violet: 'Assisté',
      success: 'Validé',
      warning: 'En revue',
      danger: 'Rejeté',
    },
    chips: {
      sections: 'sections',
      comments: 'commentaires ouverts',
      fonts: 'polices à régler',
      filter: 'Les miens seulement',
    },
    fields: {
      title: 'Titre du rapport',
      titleHint: 'Affiché sur la couverture et dans le sommaire.',
      titleValue: 'Rapport financier annuel 2025',
      period: 'Période',
      periods: {
        fy2025: 'Exercice 2025',
        fy2024: 'Exercice 2024',
      },
      entity: 'Entité publiante',
      entityError: 'Nommez l’entité dont traite le rapport.',
      note: 'Note pour les relecteurs',
      notePlaceholder: 'Ce qui a changé depuis la dernière version',
      include: 'Inclure les notes',
      some: 'Quelques pages choisies',
      outline: 'Entourer les blocs de texte',
      align: 'Alignement',
      alignments: {
        left: 'À gauche',
        center: 'Centré',
        right: 'À droite',
      },
    },
    callouts: {
      blueTitle: 'Importé en composants',
      blueBody: 'Des titres, textes et tableaux à modifier et faire recouler comme toute page.',
      action: 'Ouvrir',
      cyanTitle: '20 pages lues',
      cyanBody: '6 recoulent en colonnes, 14 sont mises en page librement.',
      violetTitle: 'Styles nommés par l’assistant',
      warningTitle: '2 polices sont incomplètes',
      warningBody: 'Importez les fichiers de police avant de réécrire le texte.',
      dangerTitle: 'L’export n’a pas abouti',
      dangerBody: 'Le service n’a pas répondu. Réessayez dans un instant.',
    },
    spinner: 'Chargement',
    meter: 'Avancement',
    empty: {
      title: 'Rien sur cette page pour le moment',
      body: 'Ajoutez une section depuis le panneau, ou importez un document.',
      action: 'Ajouter une section',
    },
    stats: {
      revenue: 'Chiffre d’affaires',
      billions: 'Md€',
      revenueHint: 'Porté par les nouveaux contrats en Île-de-France.',
      margin: 'Marge opérationnelle',
      emissions: 'Émissions des scopes 1 et 2 depuis 2015',
    },
    kicker: 'Étape de l’import',
    table: {
      caption: 'Chiffres clés',
      description: 'En millions d’euros.',
      item: 'Poste',
      revenue: 'Chiffre d’affaires',
      ebitda: 'EBE',
      net: 'Résultat net part du Groupe',
    },
    tabs: {
      contents: 'Sommaire',
      layout: 'Mise en page',
      comments: 'Commentaires',
      contentsBody: 'Les sections du rapport, dans l’ordre.',
      layoutBody: 'Papier, marges, typographie et couleurs.',
      commentsBody: 'Ce que les relecteurs ont demandé, et ce qui a été répondu.',
    },
    dialog: {
      open: 'Renommer le rapport',
      title: 'Renommer le rapport',
      description: 'Le nouveau nom apparaît dans la liste des rapports.',
      label: 'Nom',
      confirm: 'Renommer',
    },
  },
} as const

/**
 * The same shape, with every message widened back to `string`.
 *
 * `as const` above narrows each value to its own literal type, which is what
 * makes the keys exact. Left that way it would also demand that the English
 * file say "Enregistrer". This keeps the structure required and lets the words
 * differ, which is the entire point.
 */
type Translated<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : Translated<T[K]>
}

/**
 * What every language has to supply.
 *
 * Derived from the French, so adding a key here makes its absence elsewhere a
 * compile error rather than a blank space somebody notices in production.
 */
export type Messages = Translated<typeof fr>
